import {
  useEffect,
  useState,
  useRef,
  type ChangeEvent,
  type RefObject,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { useStores } from "../../../use-stores";
import {
  Modal,
  useTheme,
  IconButton,
  Backdrop,
  Dialog,
  Paper,
} from "@mui/material";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid2,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from "@mui/material";
import { useNotification } from "../../../utils/NotificationsContext";
import AddIcon from "@mui/icons-material/Add";
import CallIcon from "@mui/icons-material/Call";
import CloseIcon from "@mui/icons-material/Close";
import CopyAll from "@mui/icons-material/CopyAll";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DownloadIcon from "@mui/icons-material/Download";
import LaunchIcon from "@mui/icons-material/Launch";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SelectAllIcon from "../../../assets/select-all.png";
import ImageAPI from "../../../api/ImageAPI";
import OrderAPI from "../../../api/OrderAPI";
import {
  OrderProduct,
  OrderServices,
  PackProduct,
} from "../../../models/Order/OrderProduct";
import {
  OrderInvoiceStatus,
  OrderPaymentStatus,
  orderStatusOptions,
} from "../../../models/Order/OrderDetails";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import ImageUpload from "../../components/common/upload/ImageUpload";
import OrderServiceTable from "../../components/common/table/OrderServiceTable";
import OrderProducts from "../../components/common/table/OrderProducts";
import AppSearchDropdownMulti from "../../components/common/filter/AppSearchDropdownMulti";
import SelectAddressPopup from "../../components/common/pop-up/SelectAddressPopup";
import CustomTextField from "../../components/common/textFields/TextField";
import AddProductPopup from "./AddProductsPopup";
import AddPackConfigPopup from "./AddPackConfigPopup";
import AddServicePopup from "./AddServicesPopUp";
import AddEditAddress from "../../components/address/AddEditAddress";
import ExchangeRatePopup from "../../components/order/ExchangeRatePopup";
import InvoiceListingTable from "../../components/common/table/InvoiceListingTable";
import {
  Invoice,
  InvoicePaymentRequest,
} from "../../../models/Invoice/Invoice";
import SingleInput from "../../components/common/pop-up/SingleInput";
import ConfirmationPopup from "../../components/common/pop-up/ConfirmationPopup";
import OrderCalculationsTable from "../../components/order/OrderCalculationsTable";
import OrderPaymentLinksTable from "../../components/order/OrderPaymentLinksTable";
import FormTextField from "../../components/common/form/FormTextField";
import AddCustomer from "../customers/AddCustomer";
import { Customer } from "../../../models/Customer/Customer";
import { NewUser } from "../../../models/User";
import AddUser from "../users/AddUser";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import RaiseStockCheckReqPopup from "../../components/order/RaiseStockCheckReqPopup";
import {
  Product,
  Stock,
  DeleteSuggestedProductParams,
  StockCheckSuggestedProducts,
} from "../../../models/Stock/Stock";
import StockCheckRequestTable from "../../components/order/StockCheckRequestTable";
import StockCheckDetailsPopup from "../../components/order/StockCheckDetailsPopup";
import ProjectRequestPopup from "../../components/order/ProjectRequestPopup";
import ProjectRequestTable from "../../components/order/ProjectRequestTable";
import OrderUnifiedMockupModal, {
  OrderMockupModalTab,
} from "../../components/projects/sourcing-ops/OrderUnifiedMockupModal";
import { MockupComment } from "../../../models/Order/MockupComment";
import { ProjectRequest } from "../../../models/Project/Project";
import {
  ProjectRequestAction,
  ProjectRequestActionType,
  ProjectRequestStatusId,
} from "../../../models/Project/ProjectRequestStatus";
import PackConfigTable from "../../components/common/table/PackConfigTable";
import AdvancePaymentTable from "../../components/common/table/AdvancePaymentTable";
import { AdvancePayment } from "../../../models/Order/AdvancePayment";
import AddPaymentInfoPopup from "./invoice/AddPaymentInfoPopup";
import { HelperAPI } from "../../../api/HelperAPI";
import ImageCarousel from "../../components/common/carousel/ImageCarousel";
import ImageGalleryPopup from "../../components/common/pop-up/ImageGalleryPopup";
import UploadClientPOPopup from "../../components/order/UploadClientPOPopup";
import ActivityLogDrawer from "../../components/common/drawer/ActivityLogDrawer";
import HistoryIcon from "@mui/icons-material/History";
import { formatDate } from "../../components/common/functions/formatDate";
import GeneratePaymentLinkPopup, {
  PaymentProviderOption,
} from "./GeneratePaymentLinkPopup";
import PaymentLinksAPI from "../../../api/PaymentLinksAPI";
import { OrderArtwork } from "../../../models/Order/OrderArtwork";
import { OrderType } from "../../../models/Order/Order";

const imageAPI = new ImageAPI();
const orderAPI = new OrderAPI();
const paymentLinksAPI = new PaymentLinksAPI();

const PAYMENT_PROVIDERS: PaymentProviderOption[] = [
  { id: "RAZORPAY", label: "Razorpay" },
  { id: "STRIPE", label: "Stripe" },
];

const ADDITIONAL_INFO_MAX_LENGTH = 500;

const AddOrder = () => {
  const theme = useTheme();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get("orderId");

  const [openAddProductsPopup, setOpenAddProductsPopup] =
    useState<boolean>(false);
  const [openAddPacksConfigurationPopup, setOpenAddPacksConfigurationPopup] =
    useState<boolean>(false);
  const [openServicesPopus, setOpenopenServicesPopus] =
    useState<boolean>(false);
  const [openAddressPopup, setOpenAddressPopup] = useState<boolean>(false);
  const [openShippingAddressPopup, setOpenShippingAddressPopup] =
    useState<boolean>(false);
  const [openEditAddressPopup, setOpenEditAddressPopup] =
    useState<boolean>(false);
  const [openExchangeRatePopup, setOpenExchangeRatePopup] =
    useState<boolean>(false);
  const [openOrderDiscountPopup, setOpenOrderDiscountPopup] =
    useState<boolean>(false);
  const [confirmationPopup, setConfirmationPopup] = useState<boolean>(false);
  const [openCustomersPopup, setOpenCustomersPopup] = useState<boolean>(false);
  const [openUsersPopup, setOpenUsersPopup] = useState<boolean>(false);
  const [isPaymentLinkDialogOpen, setIsPaymentLinkDialogOpen] =
    useState<boolean>(false);
  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState<
    string | null
  >(PAYMENT_PROVIDERS[0]?.id ?? null);
  const [isGeneratingPaymentLink, setIsGeneratingPaymentLink] =
    useState<boolean>(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [paymentLinkMessage, setPaymentLinkMessage] = useState<string | null>(
    null,
  );
  const [isPaymentLinkError, setIsPaymentLinkError] = useState<boolean>(false);
  const [isPaymentLinkCopied, setIsPaymentLinkCopied] =
    useState<boolean>(false);
  const [paymentLinkPercentage, setPaymentLinkPercentage] =
    useState<number>(100);

  const [isLogoUploadLoading, setIsLogoUploadLoading] = useState(false);
  const logoUploadInputRef = useRef<HTMLInputElement>(null);
  const [popupType, setPopupType] = useState<
    | "billing"
    | "shipping"
    | "addBilling"
    | "addShipping"
    | "addBillFrom"
    | undefined
  >();
  const [editAddressId, setEditAddressId] = useState<number | undefined>(
    undefined,
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingForPopup, setIsLoadingForPopup] = useState<boolean>(false);
  const [isSelectAddressLoading, setIsSelectAddressLoading] =
    useState<boolean>(false);
  const [isSelectShippingAddressLoading, setIsSelectShippingAddressLoading] =
    useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSelectBillFromAddressLoading, setIsSelectBillFromAddressLoading] =
    useState<boolean>(false);
  const [openBillFromAddressPopup, setOpenBillFromAddressPopup] =
    useState<boolean>(false);
  const { showError, showSuccess, showWarning } = useNotification();
  const {
    customerStore,
    orderStore,
    userStore,
    addressStore,
    invoiceStore,
    stockStore,
    projectStore,
    productStore,
    mockupConfigStore,
    projectSourcingOpsStore,
  } = useStores();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchText, setSearchText] = useState("");

  const navigate = useNavigate();
  let apiTimer: any;

  const customerFieldRef = useRef<HTMLDivElement>(null);
  const userFieldRef = useRef<HTMLDivElement>(null);
  const orderLabelFieldRef = useRef<HTMLDivElement>(null);
  const orderTypeFieldRef = useRef<HTMLDivElement>(null);
  const orderLineItemsSectionRef = useRef<HTMLDivElement>(null);
  const additionalInfoSectionRef = useRef<HTMLDivElement>(null);

  const [orderFieldErrors, setOrderFieldErrors] = useState({
    customer: false,
    user: false,
    orderLabel: false,
    orderType: false,
    productsOrServices: false,
    additionalInfo: false,
  });

  const [deleteOrderConfirmationPopup, setDeleteOrderConfirmationPopup] =
    useState<boolean>(false);
  const [mockupModalOpen, setMockupModalOpen] = useState(false);
  const [mockupModalTab, setMockupModalTab] = useState<OrderMockupModalTab>("view");
  const [openImageGalleryModal, setOpenImageGalleryModal] = useState(false);
  const [mockupProductDetails, setMockupProductDetails] = useState<{
    id: number;
    name: string;
    image_url?: string;
  } | null>(null);

  const [isCommentsFetching, setIsCommentsFetching] = useState(false);
  const [isMainMockupImage, setIsMainMockupImage] = useState(false);

  const [generatorMockupId, setGeneratorMockupId] = useState<number | undefined>();

  const [openRaiseStockCheckReqPopup, setOpenRaiseStockCheckReqPopup] =
    useState<boolean>(false);
  const [openRaiseStockCheckDetailsPopup, setOpenRaiseStockCheckDetailsPopup] =
    useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedStockCheckRequestId, setSelectedStockCheckRequestId] =
    useState<number | null>(null);

  const [openProjectReqPopup, setOpenProjectReqPopup] =
    useState<boolean>(false);

  const [approveOrRejectProjectReqData, setApproveOrRejectProjectReqData] =
    useState<{
      requestId: number | null;
      action: ProjectRequestActionType | null;
      isOpen: boolean;
    }>({
      requestId: null,
      action: null,
      isOpen: false,
    });

  const [packForImageUpload, setPackForImageUpload] =
    useState<PackProduct | null>(null);

  const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
  const [isActivityLoading, setIsActivityLoading] = useState(false);

  const handleOpenActivityDrawer = () => {
    if (orderId) {
      setIsActivityLoading(true);
      setIsActivityDrawerOpen(true);
      orderStore
        .getOrderActivityLogs(Number(orderId))
        .catch((err) => {
          console.log("error in getOrderActivityLogs", err);
        })
        .finally(() => {
          setIsActivityLoading(false);
        });
    }
  };

  const uploadMedia = async (
    files: File[],
    type: "image",
  ): Promise<string[]> => {
    if (files.length === 0) return [];
    if (type === "image") {
      return imageAPI.uploadImage(files, "product-images");
    }
    return [];
  };
  const handleUpload = async (files: File[], packId: number) => {
    setIsLoading(true);
    uploadMedia(files, "image")
      .then((uploadedUrls) => {
        if (uploadedUrls.length > 0 && orderId) {
          return orderStore.uploadPackImage(
            Number(orderId),
            packId,
            uploadedUrls[0],
          );
        }
      })
      .then(async () => {
        if (orderId) {
          try {
            await orderStore.getPacks(Number(orderId));
          } catch (error) {
            showError("Error", "Error occured while fetching packs");
          }
        }
      })
      .then(() => {
        // Update the packForImageUpload state with the refreshed pack data
        if (packForImageUpload && packForImageUpload.id === packId) {
          const updatedPack = orderStore.packs.find(
            (pack) => pack.id === packId,
          );
          if (updatedPack) {
            setPackForImageUpload(updatedPack);
          }
        }
        showSuccess("Image uploaded successfully!");
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload media";
        showError("Upload Error", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const [
    openProjectRequestConfirmationPopup,
    setOpenProjectRequestConfirmationPopup,
  ] = useState(false);

  const [openUpdateAllQuantitiesPopup, setOpenUpdateAllQuantitiesPopup] =
    useState<boolean>(false);

  const [isCopied, setIsCopied] = useState(false);
  const [copiedField, setCopiedField] = useState<"phone" | "email" | null>(
    null,
  );
  const [canAssignCustomer, setCanAssignCustomer] = useState(false);

  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [openUploadClientPOPopup, setOpenUploadClientPOPopup] =
    useState<boolean>(false);

  useEffect(() => {
    if (
      !orderStore.orderRequest.order_products ||
      orderStore.orderRequest.order_products.length === 0
    ) {
      return;
    }

    const productIds = orderStore.orderRequest.order_products.map(
      (product) => product.id,
    );
    productStore
      .lastSoldProductsDetails(productIds)
      .then((response) => { })
      .catch((error) => {
        showError("Error", "Error occured while fetching last sold prices");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderStore.orderRequest.order_products]);

  const [openAdvancePaymentPopup, setOpenAdvancePaymentPopup] =
    useState<boolean>(false);
  const [editingAdvancePayment, setEditingAdvancePayment] = useState<
    AdvancePayment | undefined
  >(undefined);

  const [confirmationPopupData, setConfirmationPopupData] = useState<{
    isOpen: boolean;
    invoice: Invoice | null;
    mode: "cancel" | "reactivate";
  }>({
    isOpen: false,
    invoice: null,
    mode: "cancel",
  });

  const handleCopy = () => {
    orderStore.orderRequest.proposal_url &&
      navigator.clipboard.writeText(orderStore.orderRequest.proposal_url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1000);
  };

  const handleCopyPhone = () => {
    const phone = orderStore.orderRequest?.user?.phone;
    if (phone) {
      navigator.clipboard.writeText(phone);
      setCopiedField("phone");
      setTimeout(() => setCopiedField(null), 1000);
    }
  };

  const handleCopyEmail = () => {
    const email = orderStore.orderRequest?.user?.email;
    if (email) {
      navigator.clipboard.writeText(email);
      setCopiedField("email");
      setTimeout(() => setCopiedField(null), 1000);
    }
  };

  const handleActionsMenuOpen = () => {
    if (!actionsAnchorRef.current) return;
    setActionsMenuAnchor(actionsAnchorRef.current);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleOpenPaymentLinkDialog = () => {
    setPaymentLink(null);
    setPaymentLinkMessage(null);
    setIsPaymentLinkError(false);
    setIsPaymentLinkCopied(false);
    setSelectedPaymentProvider(PAYMENT_PROVIDERS[0]?.id as string | null);
    setPaymentLinkPercentage(100);
    setIsPaymentLinkDialogOpen(true);
  };

  const handleClosePaymentLinkDialog = () => {
    if (!isGeneratingPaymentLink) {
      setIsPaymentLinkDialogOpen(false);
    }
  };

  const handleGeneratePaymentLink = async () => {
    if (
      !orderId ||
      !selectedPaymentProvider ||
      isGeneratingPaymentLink ||
      paymentLinkPercentage < 10
    )
      return;

    try {
      setIsGeneratingPaymentLink(true);
      setPaymentLinkMessage(null);
      setIsPaymentLinkError(false);
      setIsPaymentLinkCopied(false);

      const response = await paymentLinksAPI.generatePaymentLink(
        Number(orderId),
        selectedPaymentProvider,
        paymentLinkPercentage,
      );

      if (response.success) {
        const generatedLink =
          (response.result && response.result.payment_link) || "";
        setPaymentLink(generatedLink || null);
        setPaymentLinkMessage(
          response.message || "Payment link generated successfully.",
        );
        setIsPaymentLinkError(false);
      } else {
        setPaymentLink(null);
        setPaymentLinkMessage(
          response.message || "Failed to generate payment link.",
        );
        setIsPaymentLinkError(true);
      }
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to generate payment link.";
      setPaymentLink(null);
      setPaymentLinkMessage(apiMessage);
      setIsPaymentLinkError(true);
    } finally {
      setIsGeneratingPaymentLink(false);
    }
  };

  const handleActionSelect = (actionId: string) => {
    switch (actionId) {
      case "copy_proposal_link":
        handleCopy();
        break;
      case "upload_client_po":
        setOpenUploadClientPOPopup(true);
        break;
      case "mark_as_sez":
        handleSEZToggle(orderStore.orderRequest?.is_sez === 1 ? false : true);
        break;
      case "mark_as_international_order":
        handleInternationalOrderToggle(
          orderStore.orderRequest?.international_order === 1 ? false : true,
        );
        break;
      case "mark_as_monthly_invoice":
        handleMonthlyInvoiceToggle(
          orderStore.orderRequest?.monthly_invoicing === 1 ? false : true,
        );
        break;
      default:
        break;
    }
    handleActionsMenuClose();
  };

  const handleSaveClientPO = (poNumber: string, poFileUrl: string | null) => {
    if (!orderId) {
      showError("Error", "Order ID is required");
      return;
    }

    if (!poFileUrl) {
      showError("Error", "PO file URL is required");
      return;
    }

    // Update the PO number and file URL in the order store
    setIsLoading(true);
    orderStore
      .updateClientPODetails(Number(orderId), poNumber, poFileUrl)
      .then(() => {
        showSuccess("Client PO details updated successfully!");
        setOpenUploadClientPOPopup(false);
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (projectStore.orderType.length === 0) {
      projectStore.getTypeOfOrder().catch((err) => {
        showError("Error", "Error occured while fetching order types");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderId) {
      orderStore.getProposalStats(Number(orderId)).catch((err) => {
        console.log("Error", "Error occured while fetching proposal stats");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await orderStore.getPacks(Number(orderId));
      } catch (error) {
        console.log("Error", "Error occured while fetching packs");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    setIsLoading(true);
    setCanAssignCustomer(
      userStore.hasPermission(EmployeeRoles.ASSIGN_CUSTOMER_TO_ORDER),
    );

    if (orderId && userStore.hasPermission(EmployeeRoles.VIEW_ORDER)) {
      orderStore
        .getOrderDetails(Number(orderId))
        .catch(() => {
          setIsLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      orderStore.reset();
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      orderStore.setOrderArtworks([]);
      return;
    }

    refreshOrderLogos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const handleOpenEditAddressPopup = (
    type: "billing" | "shipping" | "addBilling" | "addShipping" | "addBillFrom",
  ) => {
    if (type === "billing") {
      setEditAddressId(orderStore.orderRequest.billing_address?.id);
      setPopupType("billing");
    } else if (type === "shipping") {
      setEditAddressId(orderStore.orderRequest.shipping_address?.id);
      setPopupType("shipping");
    } else if (type === "addBilling") {
      setEditAddressId(undefined);
      setPopupType("addBilling");
    } else if (type === "addShipping") {
      setEditAddressId(undefined);
      setPopupType("addShipping");
    } else if (type === "addBillFrom") {
      setEditAddressId(undefined);
      setPopupType("addBillFrom");
    }

    setOpenEditAddressPopup(true);
  };

  const handleCustomerSearch = (searchText?: string) => {
    setSearchText(searchText || "");
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        customerStore
          .searchCustomers({ page_no: 1, search_text: searchText })
          .catch((err) => {
            showError("Error", "Error occured while fetching customers");
          });
      }, 1000);
  };

  const fetchBillFromAddress = async (customer_id: number) => {
    addressStore
      .getDefaultBillFromAddress(customer_id)
      .then(() => {
        if (addressStore.address) {
          orderStore.updateBillFromAddress(addressStore.address);
        }
      })
      .catch((err) => {
        console.log("Error", "Error occured while fetching bill from address");
      });
  };

  const searchUserByCustomerId = (customerId: number) => {
    userStore
      .getUsers({
        limit: 100,
        customer_id: customerId,
      })
      .catch((err) => {
        showError("Error", "Error occured while fetching users");
      });
  };

  useEffect(() => {
    if (orderStore.orderRequest.customer_id) {
      orderStore.clearBillingAddress();
      orderStore.clearShippingAddress();
      searchUserByCustomerId(Number(orderStore.orderRequest.customer_id));
      if (!orderId && orderStore.orderRequest.customer_id) {
        fetchBillFromAddress(Number(orderStore.orderRequest.customer_id));
      }

      if (
        Number(orderStore.orderRequest?.customer_id) ===
        Number(orderStore.existingOrder?.customer_id)
      ) {
        if (orderStore.existingOrder?.billing_address) {
          orderStore.updateBillingAddress(
            orderStore.existingOrder?.billing_address,
          );
        }

        if (orderStore.existingOrder?.shipping_address) {
          orderStore.updateShippingAddress(
            orderStore.existingOrder?.shipping_address!,
          );
        }
      }
    } else {
      orderStore.clearBillingAddress();
      orderStore.clearShippingAddress();
      orderStore.selectUser([]);
      orderStore.updateUser();
      addressStore.clearAddresses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderStore.orderRequest.customer_id]);

  const fetchStockChecEligibleProducts = () => {
    if (!orderId) return;

    setIsLoading(true);
    stockStore
      .getEligibleProducts(Number(orderId))
      .then((res) => {
        setOpenRaiseStockCheckReqPopup(!openRaiseStockCheckReqPopup);
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteProduct = (id: number) => {
    setIsLoading(true);
    orderStore
      .deleteProduct(id, orderId ? Number(orderId) : undefined)
      .then()
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeletePack = (id: number) => {
    setIsLoading(true);
    orderStore
      .deletePack(id, orderId ? Number(orderId) : 0)
      .then(async () => {
        // Re-fetch the packs to refresh the table
        try {
          await orderStore.getPacks(Number(orderId));
        } catch (error) {
          showError("Error", "Error occured while fetching packs");
        }
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUpdateProductDetails = (
    id: number,
    value: number,
    field: string,
  ) => {
    setIsLoading(true);
    orderStore
      .updateProductQuantity(
        id,
        value,
        field,
        orderId ? Number(orderId) : undefined,
      )
      .then()
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddProducts = (selectedPoducts: OrderProduct[]) => {
    setIsLoadingForPopup(true);
    orderStore
      .addProducts(selectedPoducts, Number(orderId))
      .then(() => {
        setOpenAddProductsPopup(false);
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoadingForPopup(false);
      });
  };

  const handleAddPacks = (packs: {
    label: string;
    quantity: number;
    products: { product_id: number; quantity: number }[];
  }) => {
    setIsLoadingForPopup(true);

    orderStore
      .addPacks(packs.products, packs.label, packs.quantity, Number(orderId))
      .then(async () => {
        setOpenAddPacksConfigurationPopup(false);
        showSuccess("Packs added successfully!");
        try {
          await orderStore.getPacks(Number(orderId));
        } catch (error) {
          showError("Error", "Error occured while fetching packs");
        }
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => setIsLoadingForPopup(false));
  };

  const toggleAddPacksPopup = () => {
    setOpenAddPacksConfigurationPopup((prev) => !prev);
  };

  const handleAddServices = (selectedServices: OrderServices[]) => {
    setIsLoadingForPopup(true);
    orderStore
      .addServices(selectedServices, Number(orderId))
      .then(() => {
        setOpenopenServicesPopus(false);
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoadingForPopup(false);
      });
  };

  const handleUpdateServiccesDetails = (
    id: number,
    custom_name: string,
    value: number,
    field: string,
  ) => {
    setIsLoading(true);
    orderStore
      .updateServicesQuantity(
        id,
        custom_name,
        value,
        field,
        orderId ? Number(orderId) : undefined,
      )
      .then()
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteService = (id: number, custom_name: string) => {
    setIsLoading(true);
    orderStore
      .deleteService(id, custom_name, orderId ? Number(orderId) : undefined)
      .then()
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const validateOrderRequiredFields = (): boolean => {
    const req = orderStore.orderRequest;
    const hasCustomer = Boolean(req.customer_id?.toString().trim());
    const hasUser = Boolean(req.user_id?.toString().trim());
    const hasLabel = Boolean((req.label ?? "").trim());
    const typeIdStr = String(req.type_id ?? "");
    const hasType =
      req.type_id !== undefined &&
      req.type_id !== null &&
      typeIdStr !== "" &&
      typeIdStr !== "0";
    const hasProducts = (req.order_products?.length ?? 0) > 0;
    const hasServices = (req.order_services?.length ?? 0) > 0;
    const hasLineItems = hasProducts || hasServices;
    const comments = req.comments ?? "";
    const additionalInfoTooLong = comments.length > ADDITIONAL_INFO_MAX_LENGTH;

    const errors = {
      customer: !hasCustomer,
      user: !hasUser,
      orderLabel: !hasLabel,
      orderType: !hasType,
      productsOrServices: !hasLineItems,
      additionalInfo: additionalInfoTooLong,
    };

    setOrderFieldErrors(errors);

    const fieldOrder = [
      "customer",
      "user",
      "orderLabel",
      "orderType",
      "productsOrServices",
      "additionalInfo",
    ] as const;

    const firstInvalid = fieldOrder.find((key) => errors[key]);

    const valid = !Object.values(errors).some(Boolean);
    if (!valid) {
      if (errors.additionalInfo) {
        showError(
          "Validation",
          `Additional Info cannot exceed ${ADDITIONAL_INFO_MAX_LENGTH} characters.`,
        );
      }
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.vibrate === "function"
      ) {
        navigator.vibrate(120);
      }
      if (firstInvalid) {
        const refMap: Record<
          (typeof fieldOrder)[number],
          RefObject<HTMLDivElement | null>
        > = {
          customer: customerFieldRef,
          user: userFieldRef,
          orderLabel: orderLabelFieldRef,
          orderType: orderTypeFieldRef,
          productsOrServices: orderLineItemsSectionRef,
          additionalInfo: additionalInfoSectionRef,
        };
        window.setTimeout(() => {
          refMap[firstInvalid].current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 0);
      }
    }
    return valid;
  };

  const CreateOrUpdateOrder = () => {
    if (!validateOrderRequiredFields()) {
      return;
    }
    setOrderFieldErrors({
      customer: false,
      user: false,
      orderLabel: false,
      orderType: false,
      productsOrServices: false,
      additionalInfo: false,
    });
    setIsLoading(true);

    const handleOrderCreation = () => {
      if (!orderStore.orderRequest.bill_from_address_id) {
        showError("Error Occurred", "Bill From Address is required");
        setIsLoading(false);
        return;
      }

      orderStore
        .createOrUpdateOrder(Number(orderId))
        .then(async (res) => {
          if (res) {
            navigate(`/orders/add-orders?orderId=${res}`);
            window.location.reload();
          } else {
            await orderStore.getOrderDetails(Number(orderId));
            showSuccess(`Order updated successfully!`);
          }
        })
        .catch((err) => {
          orderStore.setOrderDetails();
          showError(
            "Error occurred",
            err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
          );
        })
        .finally(() => setIsLoading(false));
    };
    handleOrderCreation();
  };

  const handleUpdateOrderDiscount = (discount: number) => {
    setIsLoading(true);
    orderStore
      .updateOrderDiscount(Number(orderId), discount)
      .then()
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUpdateExchangeRate = (discount: number, currency: string) => {
    setIsLoading(true);
    orderStore
      .addExchangeRate(Number(orderId), discount, currency)
      .then()
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDuplicateOrder = () => {
    setIsLoading(true);
    orderStore
      .duplicateOrder(Number(orderId))
      .then((res) => {
        showSuccess(`Order duplicated successfully! ${"Order Id -" + res}`);
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const recalculateOrder = () => {
    setIsLoading(true);
    orderStore
      .recalculateOrder(Number(orderId))
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteOrder = () => {
    setIsLoading(true);
    orderStore
      .deleteOrder(Number(orderId))
      .then((res) => {
        navigate("/orders/listing");
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSendTaxInvoice = () => {
    setIsLoading(true);
    orderStore
      .sendTaxInvoice(Number(orderId))
      .then((res) => {
        showSuccess(`success`);
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadPerformaInvoice = () => {
    setIsLoading(true);
    orderStore
      .downloadPerformaInvoice(Number(orderId))
      .then((response) => {
        if (response.data) {
          const blob = new Blob([response.data], { type: "application/pdf" });
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `Performa Invoice.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          showWarning("Failed to download Performa invoice.");
        }
      })
      .catch(async (err) => {
        const json = JSON.parse(await err?.response?.data?.text());
        showError(
          "Error",
          json.message || "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadPAndL = () => {
    setIsLoading(true);
    orderStore
      .downloadPAndL(Number(orderId))
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename ? response.filename : `P&L${orderId}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download P&L.");
        }
      })
      .catch(async (err) => {
        const json = JSON.parse(await err?.response?.data?.text());
        showError(
          "Error",
          json.message || "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadOldInvoice = (invoice: Invoice) => {
    setIsLoading(true);
    invoiceStore
      .downloadOldInvoice(invoice.id)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `Invoice_${invoice.id}_${orderId}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download invoice.");
        }
      })
      .catch((err) => {
        showError(
          "Error Occurred",
          err?.message || "Failed to download invoice, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const downloadInvoice = (invoice: Invoice) => {
    setIsLoading(true);
    invoiceStore
      .downloadInvoice(invoice.id)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `Invoice_${invoice.id}_${orderId}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download invoice.");
        }
      })
      .catch((err) => {
        showError(
          "Error Occurred",
          err?.message || "Failed to download invoice, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const deleteInvoice = (invoice: Invoice, reason: string) => {
    setIsLoading(true);
    invoiceStore
      .deleteInvoice(Number(orderId), invoice.id, undefined, undefined, reason)
      .then((res) => {
        orderStore.removeInvoiceLocally(Number(invoice.id), reason);
      })
      .catch((err) => {
        showError(
          "Error Occurred",
          err?.response?.data?.message ||
          err?.message ||
          "Failed to delete invoice, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const reactivateInvoice = (invoice: Invoice, reason: string) => {
    setIsLoading(true);
    invoiceStore
      .reactivateInvoice(
        Number(orderId),
        invoice.id,
        reason?.trim() ? reason.trim() : undefined,
      )
      .then((res) => {
        orderStore.reactivateInvoiceLocally(Number(invoice.id), res);
      })
      .catch((err) => {
        showError(
          "Error Occurred",
          err?.response?.data?.message ||
          err?.message ||
          "Failed to reactivate invoice, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderAddress = (...fields: (string | null | undefined)[]) =>
    fields.filter((field) => field).join(", ");

  const handleSelectClick = (addressType: string) => {
    if (
      // addressStore.addresses.length === 0 &&
      orderStore.orderRequest.customer_id
    ) {
      if (addressType === "shippingAddress") {
        setIsSelectShippingAddressLoading(true);
      } else if (addressType === "billFrom") {
        setIsSelectBillFromAddressLoading(true);
      } else {
        setIsSelectAddressLoading(true);
      }
      addressStore
        .getAddress({
          customer_id:
            addressType === "billFrom"
              ? -1
              : Number(orderStore.orderRequest.customer_id),
        })
        .then(() => { })
        .catch((err) => {
          showError("Error", "Error occured while fetching addresses");
        })
        .finally(() => {
          setIsSelectAddressLoading(false);
          setIsSelectShippingAddressLoading(false);
          setIsSelectBillFromAddressLoading(false);
          if (addressType === "shippingAddress") {
            setOpenShippingAddressPopup(!openShippingAddressPopup);
          } else if (addressType === "billFrom") {
            setOpenBillFromAddressPopup(!openBillFromAddressPopup);
          } else {
            setOpenAddressPopup(!openAddressPopup);
          }
        });
    } else {
      if (addressType === "shippingAddress") {
        setOpenShippingAddressPopup(!openShippingAddressPopup);
      } else if (addressType === "billFrom") {
        setOpenBillFromAddressPopup(!openBillFromAddressPopup);
      } else {
        setOpenAddressPopup(!openAddressPopup);
      }
    }
  };

  const toggleMockupsModal = () => {
    if (mockupModalOpen) {
      closeProductMockupModal();
    } else {
      setMockupModalOpen(true);
      setMockupModalTab("view");
    }
  };

  const loadMockupGeneratorData = (productId: number) => {
    mockupConfigStore.resetGeneratedMockupData();
    const catalogueChanged = mockupConfigStore.catalogueId !== productId;
    if (catalogueChanged) {
      mockupConfigStore.resetProductData();
      mockupConfigStore.setCatalogueId(productId);
    }

    void mockupConfigStore.getPlacementTypes().catch(() => { });
    void mockupConfigStore.getPSDTemplates(productId).catch(() => { });

    const customerId =
      Number(orderStore.orderRequest.customer_id) ||
      orderStore.existingOrder?.customer?.id;
    void projectSourcingOpsStore
      .loadMockupGeneratorLogos(customerId)
      .catch(() => {
        // Logo library may be unavailable; local upload still works.
      });
  };

  const closeProductMockupModal = () => {
    setMockupModalOpen(false);
    setMockupModalTab("view");
    setGeneratorMockupId(undefined);
    projectSourcingOpsStore.resetMockupGeneratorLogos();
    projectSourcingOpsStore.setGeneratingOrderMockup(false);
    projectSourcingOpsStore.setUpdatingOrderMockupId(null);
  };

  const handleMockupTabChange = (tab: OrderMockupModalTab, mockupId?: number) => {
    setMockupModalTab(tab);
    setGeneratorMockupId(tab === "view" ? undefined : mockupId);
    const productId = mockupProductDetails?.id;
    if (tab !== "view" && productId) {
      loadMockupGeneratorData(productId);
    }
  };

  const handleViewMockupOfProduct = (productId: number) => {
    loadMockupGeneratorData(productId);
    setIsMainMockupImage(true);
    orderStore.mockupComment = [];
    setIsLoading(true);
    if (orderStore.orderRequest.order_id) {
      orderStore
        .fetchProductMockups(orderStore.orderRequest.order_id, productId)
        .then(() => {
          if (!mockupModalOpen) {
            setMockupModalOpen(true);
            setMockupModalTab("view");
          }
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to fetch mockups, please try again later",
          );
        })
        .finally(() => {
          setIsLoading(false);
          setIsMainMockupImage(false);
        });
    } else {
      setIsLoading(false);
      setIsMainMockupImage(false);
      showError("Error Occurred", "Order Id not found");
    }
  };

  const uploadMockupImages = (productId: number, images: string[]) => {
    setIsMainMockupImage(true);
    if (productId && images.length > 0 && orderStore.orderRequest.order_id) {
      orderStore
        .uploadMockupsForProduct(
          {
            product_id: productId,
            image_urls: images,
          },
          Number(orderId),
        )
        .then(() => {
          if (orderStore.orderRequest.order_id) {
            handleViewMockupOfProduct(productId);
          } else {
            showError("Error Occurred", "Order Id not found");
          }
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to upload mockups, please try again later",
          );
        })
        .finally(() => {
          setIsLoading(false);
          setIsMainMockupImage(false);
        });
    } else {
      setIsMainMockupImage(false);
      showError(
        "Error Occurred",
        "Failed to find product id or mockups or order id",
      );
    }
  };

  const postMockupComment = (mockupComment: MockupComment) => {
    if (mockupComment.comments && mockupComment.mockup_id) {
      orderStore
        .postCommentForMockup(mockupComment)
        .then(() => {
          orderStore.updateCommentLocally(mockupComment);
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to send comment, please try again later",
          );
        })
        .finally(() => {
          setIsCommentsFetching(false);
        });
    } else {
      setIsCommentsFetching(false);
      showError("Error Occurred", "Failed to find comment OR mockup id");
    }
  };

  const fetchMockupComment = (mockupId: number) => {
    if (mockupId) {
      orderStore
        .fetchCommentsForProductMockups(mockupId)
        .then()
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to fetch comment, please try again later",
          );
        })
        .finally(() => {
          setIsCommentsFetching(false);
        });
    } else {
      setIsCommentsFetching(false);
      showError("Error Occurred", "Failed to find mockup id");
    }
  };

  const deleteMockup = (mockupId: number) => {
    setIsMainMockupImage(true);
    if (mockupId) {
      orderStore
        .deleteMockup(mockupId, Number(orderId))
        .then(() => {
          if (orderStore.productMockup.length > 0) {
            orderStore.removeMockupImageLocally(mockupId);
          } else {
            showSuccess("Please refresh your page to see the updates");
          }
        })
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError(
            "Error Occurred",
            errorMessage || "Failed to fetch comment, please try again later",
          );
        })
        .finally(() => {
          setIsMainMockupImage(false);
          setIsLoading(false);
        });
    } else {
      setIsMainMockupImage(false);
      setIsLoading(false);
      showError("Error Occurred", "Failed to find mockup id");
    }
  };

  const completeMockupWorkflow = () => {
    const productId = mockupProductDetails?.id;
    setGeneratorMockupId(undefined);
    projectSourcingOpsStore.resetMockupGeneratorLogos();
    projectSourcingOpsStore.setGeneratingOrderMockup(false);
    projectSourcingOpsStore.setUpdatingOrderMockupId(null);

    if (productId && orderStore.orderRequest.order_id) {
      orderStore
        .fetchProductMockups(orderStore.orderRequest.order_id, productId)
        .then(() => {
          setMockupModalTab("view");
        })
        .catch(() => {
          setMockupModalTab("view");
        });
      return;
    }
    setMockupModalTab("view");
  };

  const handleOrderGenerateMockup = async (payload: {
    psd_template_id: number;
    logoFile?: File | null;
    logoUrl?: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
  }) => {
    const productId = mockupProductDetails?.id;
    if (!productId || !orderId) {
      showError("Error", "Product or order not found");
      return;
    }

    projectSourcingOpsStore.setGeneratingOrderMockup(true);

    try {
      let logoUrl = payload.logoUrl ?? null;
      if (!logoUrl) {
        if (!payload.logoFile) {
          showError("Error", "Please upload or select a logo");
          return;
        }
        const imageAPI = new ImageAPI();
        logoUrl = await imageAPI.uploadFile(payload.logoFile, "mockup-logos");
      }
      if (!logoUrl) {
        showError("Error", "Please upload or select a logo");
        return;
      }
      const result = await orderStore.generateMockupForProduct({
        order_id: Number(orderId),
        product_id: productId,
        psd_template_id: payload.psd_template_id,
        logo_url: logoUrl,
        x: payload.x,
        y: payload.y,
        width: payload.width,
        height: payload.height,
        rotation: payload.rotation,
        ...(typeof generatorMockupId === "number" &&
          Number.isFinite(generatorMockupId)
          ? { mockup_id: generatorMockupId }
          : {}),
      });
      if (!result) return;

      completeMockupWorkflow();
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      showError(
        "Generate failed",
        err?.response?.data?.message ||
        err?.message ||
        "Failed to generate mockup",
      );
    } finally {
      projectSourcingOpsStore.setGeneratingOrderMockup(false);
    }
  };

  const handleOrderUploadLogoToLibrary = (file: File) => {
    const customerId =
      Number(orderStore.orderRequest.customer_id) ||
      orderStore.existingOrder?.customer?.id;
    void projectSourcingOpsStore
      .uploadMockupGeneratorLogo(file, customerId)
      .catch((e) => {
        showError(
          "Upload failed",
          e?.response?.data?.message || "Failed to upload logo",
        );
      });
  };

  const handleOrderDeleteUploadedLogo = async (logoId: number) => {
    const customerId =
      Number(orderStore.orderRequest.customer_id) ||
      orderStore.existingOrder?.customer?.id;
    try {
      await projectSourcingOpsStore.deleteMockupGeneratorLogo(
        logoId,
        customerId,
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showError(
        "Delete failed",
        err?.response?.data?.message || "Failed to delete logo",
      );
    }
  };

  const handleOrderManualUploadMockup = async (payload: {
    id: number;
    file: File;
  }) => {
    const productId = mockupProductDetails?.id;
    if (!productId || !orderId) return;

    projectSourcingOpsStore.setUpdatingOrderMockupId(payload.id);
    try {
      const imageAPI = new ImageAPI();
      const uploadedUrls = await imageAPI.uploadImage(
        [payload.file],
        "product-images",
      );
      const url = uploadedUrls[0];
      if (!url) {
        showError("Upload failed", "Failed to upload mockup image");
        return;
      }
      await orderStore.uploadMockupsForProduct(
        {
          product_id: productId,
          image_urls: [url],
          id: payload.id,
        },
        Number(orderId),
      );
      completeMockupWorkflow();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showError(
        "Upload failed",
        err?.response?.data?.message || "Failed to upload mockup",
      );
    } finally {
      projectSourcingOpsStore.setUpdatingOrderMockupId(null);
    }
  };

  const handleOrderUploadMockupFiles = async (files: File[]) => {
    const productId = mockupProductDetails?.id;
    if (!productId || !orderId) return;

    projectSourcingOpsStore.setGeneratingOrderMockup(true);
    try {
      const imageAPI = new ImageAPI();
      const uploadedUrls = await imageAPI.uploadImage(files, "product-images");
      if (!uploadedUrls.length) {
        showError("Upload failed", "Failed to upload mockup image");
        return;
      }
      await orderStore.uploadMockupsForProduct(
        {
          product_id: productId,
          image_urls: uploadedUrls,
        },
        Number(orderId),
      );
      completeMockupWorkflow();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      showError(
        "Upload failed",
        err?.response?.data?.message || "Failed to upload mockup",
      );
    } finally {
      projectSourcingOpsStore.setGeneratingOrderMockup(false);
    }
  };

  useEffect(() => {
    getStockListing();
    getProjectListing();
    getAdvancePayments();
    addressStore.clearAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    getStockListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderStore.orderRequest.products]);

  const handleStockCheckrequest = (data: Stock[]) => {
    setIsLoading(true);
    stockStore
      .raiseStockCheckRequest(Number(orderId), data)
      .then((res) => {
        getStockListing();
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getStockListing = () => {
    if (orderId) {
      stockStore
        .getStockListByOrderId(Number(orderId))
        .then()
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError("Error Occurred", errorMessage);
          setIsLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const deleteSuggestedProduct = (
    params: DeleteSuggestedProductParams,
  ): Promise<void> => {
    setIsLoading(true);
    return stockStore
      .deleteSuggestedProduct(params, Number(orderId))
      .then(() => { })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
        throw err;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const updateStockCheck = (
    requestId: number,
    product: Product[],
    suggestedProduct: StockCheckSuggestedProducts[],
  ) => {
    setIsLoading(true);
    stockStore
      .updateStockCheckRequest(Number(orderId), requestId, {
        products: product,
        suggestedProducts: suggestedProduct,
      })
      .then((res) => {
        getStockListing();
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const updateStockCheckRequestStatus = (requestId: number) => {
    setIsLoading(true);
    const filtered_requests = stockStore.orderStockCheckReqDetails.filter(
      (req) => req.id === requestId,
    );
    if (filtered_requests.length === 0) {
      return;
    }

    const currentStatus = filtered_requests[0].status;

    stockStore
      .updateStockCheckRequest(Number(orderId), requestId, {
        status: currentStatus,
      })
      .then(() => { })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        getStockListing();
        setIsLoading(false);
      });
  };

  const handleProjectRequest = (data: Partial<ProjectRequest>) => {
    setIsLoading(true);
    data.order_id = Number(orderId);
    projectStore
      .raiseProjectRequest(data)
      .then((res) => {
        setOpenProjectReqPopup(!openProjectReqPopup);
        getProjectListing();
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        getStockListing();
        setIsLoading(false);
      });
  };

  const getProjectListing = () => {
    if (orderId) {
      projectStore
        .getProjectRequestsByOrderId(Number(orderId))
        .then()
        .catch((err) => {
          const errorMessage = err?.response?.data?.message || err.message;
          showError("Error Occurred", errorMessage);
          setIsLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleApproveOrRejectProjectRequest = async (
    requestId: number,
    action: ProjectRequestActionType,
    cancelReason?: string,
  ) => {
    if (action === ProjectRequestAction.FINANCE_APPROVE) {
      try {
        setIsLoading(true);
        await projectStore.financeApproveRequest(requestId);
        showSuccess("Project request finance approved successfully");
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed to finance approve project request",
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (action === ProjectRequestAction.FINANCE_REJECT) {
      try {
        setIsLoading(true);
        await projectStore.financeRejectRequest(requestId, cancelReason!);
        showSuccess("Project request finance rejected successfully");
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || err.message;
        showError(
          "Error Occurred",
          errorMessage || "Failed to finance reject project request",
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (
      orderStore.existingOrder?.is_stock_check_required === true &&
      stockStore.orderStockCheckReqDetails.length === 0
    ) {
      showError(
        "Error",
        "Raise stock check request first before approving or rejecting project request.",
      );
      return;
    }
    const openStockCheckRequest = stockStore.orderStockCheckReqDetails.filter(
      (req) => req.status === 1,
    );
    if (openStockCheckRequest.length > 0) {
      showError(
        "Error",
        "Please take action on existing OPEN stock check request before proceeding.",
      );
      return;
    }
    try {
      if (action === ProjectRequestStatusId.APPROVED) {
        // Default tasks on project-request approval were removed (no createDefaultTasks).
        setIsLoading(true);
        await projectStore.approveRequest(
          requestId,
          userStore.user?.id,
          userStore.user?.name,
        );
        showSuccess("Project request approved successfully");
      } else {
        setIsLoading(true);
        await projectStore.rejectRequest(requestId, cancelReason!);
        showSuccess("Project request rejected successfully");
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message;
      showError(
        "Error Occurred",
        errorMessage || "Failed to approve or reject project request",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateAllQuantites = (quantity: number) => {
    if (quantity <= 0) {
      showError("Error Occurred", "Enter a valid quantity");
      return;
    }
    setIsLoading(true);
    orderStore
      .updateAllQuantities(Number(orderId), quantity)
      .then()
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => { });
      }, 1000);
  };

  const handleDownloadSizeMix = (fileUrl: string) => {
    setIsLoading(true);
    HelperAPI.downloadAFile(fileUrl)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");

          let filename = response.filename
            ? response.filename
            : `Size_Mix_${orderId}.csv`;

          if (!filename.endsWith(".csv")) {
            filename = filename.replace(/\.csv_?$/, "") + ".csv";
          }

          a.href = downloadUrl;
          a.download = filename;

          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download Size Mix.");
        }
      })
      .catch(async (err) => {
        const json = JSON.parse(await err?.response?.data?.text());
        showError(
          "Error",
          json.message || "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadOldInvoiceByOrderId = () => {
    setIsLoading(true);
    invoiceStore
      .downloadOldInvoiceByOrderId(Number(orderId))
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");

          let filename = response.filename
            ? response.filename
            : `Invoice_${orderId}.zip`;

          if (!filename.endsWith(".zip")) {
            filename = filename.replace(/\.zip_?$/, "") + ".zip";
          }

          a.href = downloadUrl;
          a.download = filename;

          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download invoice.");
        }
      })
      .catch((err) => {
        showError(
          "Error Occurred",
          err?.message || "Failed to download invoice, please try again later",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDownloadInvoiceByOrderId = () => {
    setIsLoading(true);
    invoiceStore
      .downloadInvoiceByOrderId(Number(orderId))
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");

          let filename = response.filename
            ? response.filename
            : `Invoice_${orderId}.zip`;

          if (!filename.endsWith(".zip")) {
            filename = filename.replace(/\.zip_?$/, "") + ".zip";
          }

          a.href = downloadUrl;
          a.download = filename;

          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download P&L.");
        }
      })
      .catch(async (err) => {
        const json = JSON.parse(await err?.response?.data?.text());
        showError(
          "Error",
          json.message || "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSEZToggle = async (isSez: boolean) => {
    setIsLoading(true);
    try {
      await orderStore.updateSezStatus(Number(orderId), isSez ? 1 : 0);
    } catch (error: any) {
      showError(
        "Error occurred",
        error?.response?.data?.message || "Failed to update SEZ status",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInternationalOrderToggle = async (
    isInternationalOrder: boolean,
  ) => {
    setIsLoading(true);
    try {
      await orderStore.updateInternationalOrderStatus(
        Number(orderId),
        isInternationalOrder ? 1 : 0,
      );
    } catch (error: any) {
      showError(
        "Error occurred",
        error?.response?.data?.message || "Failed to update SEZ status",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleMonthlyInvoiceToggle = async (isMonthlyInvoice: boolean) => {
    setIsLoading(true);
    try {
      await orderStore.updateMonthlyInvoiceStatus(
        Number(orderId),
        isMonthlyInvoice ? 1 : 0,
      );
    } catch (error: any) {
      showError(
        "Error occurred",
        error?.response?.data?.message || "Failed to update SEZ status",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getAdvancePayments = () => {
    setIsLoading(true);
    invoiceStore.getAdvancePayments(Number(orderId)).finally(() => {
      setIsLoading(false);
    });
  };

  const updateAdvancePayment = (value: InvoicePaymentRequest) => {
    value.order_id = Number(orderId);
    setIsLoading(true);

    const apiCall = editingAdvancePayment?.id
      ? invoiceStore.editAdvancePayment(Number(editingAdvancePayment.id), value)
      : invoiceStore.updateAdvancePayment(value);

    apiCall
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setOpenAdvancePaymentPopup(false);
        setEditingAdvancePayment(undefined);
        setIsLoading(false);
      });
  };

  const handleEditAdvancePayment = (payment: AdvancePayment) => {
    setEditingAdvancePayment(payment);
    setOpenAdvancePaymentPopup(true);
  };

  const handleDeletePayment = (paymentId: number) => {
    setIsLoading(true);
    invoiceStore
      .deleteAdvancePayment(Number(orderId), paymentId)
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || err.message;
        showError("Error Occurred", errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const supportedLogoExtensions = [".ai", ".eps", ".cdr", ".dst", ".emb"];

  const refreshOrderLogos = async () => {
    if (!orderId) return;

    await orderStore.fetchOrderLogos(Number(orderId)).catch(() => {
      showError("Error", "Failed to fetch logos");
    });
  };

  const getLogoUrl = (logo: OrderArtwork) => logo.image_url || logo.url || "";

  const getLogoName = (logo: OrderArtwork, index: number) => {
    const fallbackName = `logo-${index + 1}`;
    if (logo.name) return logo.name;
    const fileNameFromUrl = getLogoUrl(logo).split("/").pop();
    return fileNameFromUrl || fallbackName;
  };

  const isImageLogo = (logo: OrderArtwork) => {
    const name = getLogoName(logo, 0).toLowerCase();
    const url = getLogoUrl(logo).toLowerCase();
    return [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp"].some(
      (ext) => name.endsWith(ext) || url.endsWith(ext),
    );
  };

  const canManageLogos =
    userStore.hasPermission(EmployeeRoles.CREATE_ORDER) ||
    userStore.hasPermission(EmployeeRoles.EDIT_ORDER);

  const isSupportedLogoFile = (file: File) => {
    if (file.type.startsWith("image/")) return true;
    const lowerName = file.name.toLowerCase();
    return supportedLogoExtensions.some((ext) => lowerName.endsWith(ext));
  };

  const handleLogoUploadClick = () => {
    logoUploadInputRef.current?.click();
  };

  const handleLogoFilesSelected = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length === 0) return;

    if (!orderId) {
      showWarning("Please save the order before uploading logos.");
      event.target.value = "";
      return;
    }

    const validFiles = selectedFiles.filter(isSupportedLogoFile);
    const invalidFilesCount = selectedFiles.length - validFiles.length;

    if (invalidFilesCount > 0) {
      showWarning(
        `${invalidFilesCount} file(s) skipped. Allowed: image, .ai, .eps, .cdr, .dst, .emb`,
      );
    }

    if (validFiles.length === 0) {
      event.target.value = "";
      return;
    }

    const productId = orderStore.orderRequest.order_products?.[0]?.id;
    if (!productId) {
      showWarning("Please add a product before uploading logos.");
      event.target.value = "";
      return;
    }

    setIsLogoUploadLoading(true);
    try {
      const response = await orderAPI.addOrderLogo(
        Number(orderId),
        productId,
        validFiles,
      );
      if (response.success) {
        showSuccess(
          `${response.result.length} logo file(s) uploaded successfully.`,
        );
      } else {
        showError("Upload failed", response.message);
      }
    } catch (error: any) {
      showError(
        "Upload failed",
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload logo files",
      );
    } finally {
      setIsLogoUploadLoading(false);
      event.target.value = "";
    }
  };

  const handleDownloadLogo = (url: string, filename: string) => {
    setIsLoading(true);
    Promise.resolve()
      .then(() => {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "image";
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((error: any) => {
        console.error("Download error:", error);
        showError("Error", error?.message || "Failed to download image");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getPaymentStatusColor = (paymentStatus: OrderPaymentStatus) => {
    switch (paymentStatus) {
      case OrderPaymentStatus.PAID:
        return theme.palette.success.main;
      case OrderPaymentStatus.PARTIALLY_PAID:
        return theme.palette.info.main;
      case OrderPaymentStatus.UNPAID:
        return theme.palette.error.main;
    }
  };

  const getInvoiceStatusColor = (invoiceStatus: OrderInvoiceStatus) => {
    switch (invoiceStatus) {
      case OrderInvoiceStatus.INVOICED:
        return theme.palette.secondary.main;
      case OrderInvoiceStatus.PARTIALLY_INVOICED:
        return theme.palette.info.main;
      case OrderInvoiceStatus.NOT_INVOICED:
        return theme.palette.error.main;
    }
  };
  const duplicatedFromOrderId =
    orderStore.orderRequest?.duplicated_from_orderid;

  const ordersPageInnerSx = {
    marginTop: "0.6rem",
    paddingX: 4,
    paddingY: 2,
    boxShadow:
      "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
    borderRadius: "4px",
    minHeight: { xs: "60vh", md: window.innerHeight - 110 },
  };

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {!userStore.hasPermission([
        EmployeeRoles.VIEW_ORDER,
        EmployeeRoles.CREATE_ORDER,
        EmployeeRoles.EDIT_ORDER,
      ]) && (
          <Paper>
            <Box sx={ordersPageInnerSx}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 0.4,
                  mb: 2,
                }}
              >
                <CustomTypography variant="heading3">
                  {orderId ? `Order Details - ${orderId}` : "Add Order"}
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  color={theme.palette.customColors?.greyTextColor}
                >
                  View and manage order details.
                </CustomTypography>
              </Box>
              <PermissionBlockLabel />
            </Box>
          </Paper>
        )}
      {userStore.hasPermission([
        EmployeeRoles.VIEW_ORDER,
        EmployeeRoles.CREATE_ORDER,
        EmployeeRoles.EDIT_ORDER,
      ]) && (
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
            <Box sx={{ paddingX: 1 }}>
              <Box sx={{ paddingTop: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                    >
                      <CustomTypography variant="heading3" fontWeight={700}>
                        {orderId ? `Order Details - ${orderId}` : "Add Order"}
                      </CustomTypography>
                      {orderId &&
                        duplicatedFromOrderId !== undefined &&
                        duplicatedFromOrderId !== null && (
                          <Box
                            component="a"
                            href={`/orders/add-orders?orderId=${duplicatedFromOrderId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              mt: 0.25,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: theme.palette.text.secondary,
                              textDecoration: "none",
                              width: "fit-content",
                              cursor: "pointer",
                              transition: "color 0.2s ease",
                              "& .duplicate-link-icon": {
                                fontSize: 13,
                                color: theme.palette.text.disabled,
                                transition: "color 0.2s ease",
                              },
                              "&:hover": {
                                color: theme.palette.text.primary,
                                textDecoration: "underline",
                                "& .duplicate-link-icon": {
                                  color: theme.palette.text.primary,
                                },
                              },
                            }}
                          >
                            <LaunchIcon className="duplicate-link-icon" />
                            <CustomTypography
                              variant="body2"
                              sx={{ fontSize: "0.9rem" }}
                            >
                              Duplicated from{" "}
                              <Box
                                component="span"
                                sx={{ fontSize: "0.95rem", fontWeight: 600 }}
                              >
                                #{duplicatedFromOrderId}
                              </Box>
                            </CustomTypography>
                          </Box>
                        )}
                    </Box>
                    {orderStore.existingOrder?.project_id && (
                      <IconButton
                        onClick={() => {
                          navigate(
                            `/projects/${orderStore.existingOrder!.project_id}`,
                          );
                        }}
                        sx={{
                          color: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.main + "10",
                          width: 32,
                          height: 32,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.main + "20",
                            transform: "scale(1.1)",
                          },
                          transition: "all 0.2s ease",
                        }}
                        size="small"
                        title="View Project"
                      >
                        <LaunchIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                    {orderId && orderStore.orderRequest?.payment_status && (
                      <Chip
                        label={orderStore.orderRequest.payment_status}
                        size="small"
                        variant="outlined"
                        sx={{
                          backgroundColor: "transparent",
                          borderWidth: 2,
                          borderColor: getPaymentStatusColor(
                            orderStore.orderRequest.payment_status,
                          ),
                          borderRadius: "4px",
                          color: getPaymentStatusColor(
                            orderStore.orderRequest.payment_status,
                          ),
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      />
                    )}
                    {orderId && orderStore.orderRequest?.invoice_status && (
                      <Chip
                        label={orderStore.orderRequest.invoice_status}
                        size="small"
                        variant="outlined"
                        sx={{
                          backgroundColor: "transparent",
                          borderWidth: 2,
                          borderColor: getInvoiceStatusColor(
                            orderStore.orderRequest.invoice_status,
                          ),
                          borderRadius: "4px",
                          color: getInvoiceStatusColor(
                            orderStore.orderRequest.invoice_status,
                          ),
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      />
                    )}
                    {orderId && orderStore.orderRequest?.is_sez === 1 && (
                      <Chip
                        label="SEZ"
                        size="small"
                        variant="outlined"
                        sx={{
                          backgroundColor: "transparent",
                          borderWidth: 2,
                          borderColor: theme.palette.info.main,
                          borderRadius: "4px",
                          color: theme.palette.info.main,
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      />
                    )}
                    {orderId &&
                      orderStore.orderRequest?.international_order === 1 && (
                        <Chip
                          label="International Order"
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: "transparent",
                            borderWidth: 2,
                            borderRadius: "4px",
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            fontSize: "0.8rem",
                          }}
                        />
                      )}
                    {orderId &&
                      orderStore.orderRequest?.monthly_invoicing === 1 && (
                        <Chip
                          label="Monthly Invoiced"
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: "transparent",
                            borderWidth: 2,
                            borderRadius: "4px",
                            borderColor: theme.palette.secondary.main,
                            color: theme.palette.secondary.main,
                            fontWeight: 600,
                            fontSize: "0.8rem",
                          }}
                        />
                      )}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box>
                      {orderStore.orderRequest.proposal_code && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "4px",
                            padding: "4px 8px",
                          }}
                        >
                          <CustomTypography
                            sx={{
                              fontWeight: 500,
                            }}
                            variant="heading7"
                          >
                            Proposal Link
                          </CustomTypography>

                          <Tooltip
                            title="Link Copied!"
                            open={isCopied}
                            disableHoverListener
                            disableFocusListener
                            disableTouchListener
                          >
                            <Box
                              sx={{
                                background:
                                  theme.palette.customColors.lightPurple,
                                borderRadius: "4px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: "pointer",
                              }}
                            >
                              <IconButton
                                onClick={() => {
                                  handleCopy();
                                }}
                                sx={{ width: "25px", height: "25px" }}
                              >
                                <CopyAll sx={{ fontSize: "25px" }} />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    {orderId && (
                      <Tooltip title="Activity History">
                        <IconButton
                          onClick={handleOpenActivityDrawer}
                          sx={{
                            backgroundColor: theme.palette.primary.main + "10",
                            color: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor: theme.palette.primary.main + "20",
                            },
                          }}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {orderId &&
                      (userStore.hasPermission(EmployeeRoles.CREATE_ORDER) ||
                        userStore.hasPermission(EmployeeRoles.EDIT_ORDER)) && (
                        <Box ref={actionsAnchorRef}>
                          <AppButton
                            variant="grayscale"
                            label="Actions"
                            onClick={handleActionsMenuOpen}
                            customStyles={{ minWidth: 120 }}
                          />
                        </Box>
                      )}
                  </Box>
                </Box>

                <Menu
                  anchorEl={actionsMenuAnchor}
                  open={Boolean(actionsMenuAnchor)}
                  onClose={handleActionsMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  disableAutoFocusItem
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow:
                          "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                        minWidth: 240,
                        overflow: "hidden",
                      },
                    },
                  }}
                >
                  {orderId &&
                    (userStore.hasPermission(EmployeeRoles.CREATE_ORDER) ||
                      userStore.hasPermission(EmployeeRoles.EDIT_ORDER)) && (
                      <>
                        <MenuItem
                          onClick={() => handleActionSelect("upload_client_po")}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            gap: 1,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Upload Client PO
                          </CustomTypography>
                        </MenuItem>
                        <Divider />
                      </>
                    )}
                  {orderId &&
                    userStore.hasPermission([
                      EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
                      EmployeeRoles.CREATE_INVOICE_AND_PI,
                      EmployeeRoles.EDIT_INVOICE,
                      EmployeeRoles.EDIT_ORDER_SEZ,
                    ]) && (
                      <>
                        <MenuItem
                          onClick={() => handleActionSelect("mark_as_sez")}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            gap: 1,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            {orderStore.orderRequest?.is_sez === 1
                              ? "Unmark SEZ"
                              : "Mark SEZ"}
                          </CustomTypography>
                        </MenuItem>
                        <Divider />
                      </>
                    )}
                  {orderId &&
                    userStore.hasPermission([
                      EmployeeRoles.CREATE_INVOICE_AND_PI,
                      EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
                      EmployeeRoles.EDIT_INVOICE,
                      EmployeeRoles.EDIT_ORDER_SEZ,
                    ]) && (
                      <>
                        <MenuItem
                          onClick={() =>
                            handleActionSelect("mark_as_international_order")
                          }
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            gap: 1,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            {orderStore.orderRequest?.international_order === 1
                              ? "Unmark International Order"
                              : "Mark International Order"}
                          </CustomTypography>
                        </MenuItem>
                        <Divider />
                      </>
                    )}
                  {orderId &&
                    userStore.hasPermission([
                      EmployeeRoles.CREATE_INVOICE_AND_PI,
                      EmployeeRoles.EDIT_INVOICE,
                    ]) && (
                      <MenuItem
                        onClick={() =>
                          handleActionSelect("mark_as_monthly_invoice")
                        }
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          gap: 1,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          {orderStore.orderRequest?.monthly_invoicing === 1
                            ? "Unmark Monthly Invoice"
                            : "Mark Monthly Invoice"}
                        </CustomTypography>
                      </MenuItem>
                    )}
                </Menu>

                <GeneratePaymentLinkPopup
                  open={isPaymentLinkDialogOpen}
                  onClose={handleClosePaymentLinkDialog}
                  providers={PAYMENT_PROVIDERS}
                  selectedProviderId={selectedPaymentProvider}
                  onSelectProvider={(id) => {
                    setPaymentLinkMessage(null);
                    setIsPaymentLinkError(false);
                    setSelectedPaymentProvider(id);
                    setPaymentLink(null);
                    setIsPaymentLinkCopied(false);
                  }}
                  percentage={paymentLinkPercentage}
                  onPercentageChange={(value) =>
                    setPaymentLinkPercentage(Number(value))
                  }
                  hasMaxLinks={
                    orderStore.existingOrder?.payment_links
                      ? orderStore.existingOrder.payment_links.length >= 5
                      : false
                  }
                  isGenerating={isGeneratingPaymentLink}
                  paymentLink={paymentLink}
                  paymentLinkMessage={paymentLinkMessage}
                  isError={isPaymentLinkError}
                  isCopied={isPaymentLinkCopied}
                  onCopy={async () => {
                    if (!paymentLink) return;
                    try {
                      await navigator.clipboard.writeText(paymentLink);
                      setIsPaymentLinkCopied(true);
                      setTimeout(() => setIsPaymentLinkCopied(false), 1500);
                    } catch {
                      setIsPaymentLinkCopied(false);
                    }
                  }}
                  onGenerate={handleGeneratePaymentLink}
                />

                <Box
                  sx={{
                    height: "100%",
                    mt: 2,
                  }}
                >
                  <Card
                    sx={{
                      mb: 2,
                      boxShadow:
                        "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 1,
                        "&:last-child": { pb: 1 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          gap: 2,
                        }}
                      >
                        <CustomTypography variant="heading7">
                          Proposal Stats:
                        </CustomTypography>
                        {orderStore.proposalStats &&
                          orderStore.proposalStats.unique_visitors > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flex: 1,
                              minWidth: 0,
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <Box sx={{ display: "inline-flex" }}>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={500}
                                  color={theme.palette.text.secondary}
                                >
                                  Unique Visitors:{" "}
                                </CustomTypography>
                                <CustomTypography
                                  variant="body1"
                                  fontWeight={600}
                                >
                                  {orderStore.proposalStats.unique_visitors}
                                </CustomTypography>
                              </Box>
                              <Box sx={{ display: "inline-flex" }}>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={500}
                                  color={theme.palette.text.secondary}
                                >
                                  Total Time Spent:{" "}
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={600}
                                >
                                  {orderStore.proposalStats.total_time_spent}
                                </CustomTypography>
                              </Box>
                              <Box sx={{ display: "inline-flex" }}>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={500}
                                  color={theme.palette.text.secondary}
                                >
                                  First Visit Time:{" "}
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={600}
                                >
                                  {formatDate(
                                    orderStore.proposalStats.first_visit_time,
                                  )}
                                </CustomTypography>
                              </Box>
                              <Box sx={{ display: "inline-flex" }}>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={500}
                                  color={theme.palette.text.secondary}
                                >
                                  Last Visit Time:{" "}
                                </CustomTypography>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={600}
                                >
                                  {formatDate(
                                    orderStore.proposalStats.last_visit_time,
                                  )}
                                </CustomTypography>
                              </Box>
                            </Box>
                            <Box sx={{ display: "inline-flex" }}>
                              <IconButton
                                onClick={() => {
                                  const dipatch_url = `/report/data?title=${encodeURI(
                                    "Proposal data",
                                  )}&base_url=/v5/ops-analytics/vendors/scorecard&path=/data/proposal-data&order_id=${orderId}`;
                                  window.open(dipatch_url, "_blank");
                                }}
                                sx={{
                                  color: theme.palette.primary.main,
                                  backgroundColor:
                                    theme.palette.primary.main + "10",
                                  width: 28,
                                  height: 28,
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.primary.main + "20",
                                    transform: "scale(1.1)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                                size="small"
                                title="View Project"
                              >
                                <LaunchIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              minWidth: 0,
                              height: "28px",
                              alignItems: "center",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.text.secondary}
                              fontWeight={500}
                            >
                              No proposal stats available
                            </CustomTypography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                  {/* Order Basic Details Section - Modern Responsive Layout */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {/* Row 1: Customer Info & Upload Logos */}
                    <Grid2 container spacing={3}>
                      {/* Customer Info Card */}
                      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                        <Card
                          sx={{
                            height: "100%",
                            p: 3,
                            boxShadow:
                              "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
                          }}
                        >
                          <CardContent>
                            <CustomTypography variant="heading4" fontWeight={700}>
                              Customer Info
                            </CustomTypography>

                            {/* Customer */}
                            <Box
                              mt={3}
                              mb={1}
                              display={"flex"}
                              justifyContent={"space-between"}
                              alignItems="center"
                            >
                              <Box
                                component="span"
                                sx={{
                                  fontSize: "0.72rem",
                                  color: "#94A3B8",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                Customer *
                              </Box>
                              <AppButton
                                variant="tertiary"
                                label="Add"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  setOpenCustomersPopup(!openCustomersPopup);
                                }}
                                customStyles={{
                                  backgroundColor: "transparent",
                                  padding: 1,
                                  margin: 0,
                                  fontSize: "0.85rem",
                                  color: theme.palette.customColors.pinkDark,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: theme.palette.customColors.pinkDark,
                                    boxShadow: "none",
                                  },
                                }}
                                disabled={
                                  userStore.hasPermission(
                                    EmployeeRoles.CREATE_CUSTOMER,
                                  ) ||
                                    userStore.hasPermission(
                                      EmployeeRoles.EDIT_CUSTOMER,
                                    )
                                    ? false
                                    : true
                                }
                              />
                            </Box>
                            <Box ref={customerFieldRef}>
                              <AppSearchDropdownMulti
                                isMultiSelect={false}
                                title="Select Customer"
                                value={
                                  orderStore.orderRequest.customer_id
                                    ? [
                                      {
                                        id: String(
                                          orderStore.orderRequest.customer_id,
                                        ),
                                        value:
                                          orderStore.orderRequest
                                            .customer_name || "",
                                      },
                                    ]
                                    : []
                                }
                                list={
                                  customerStore.customers.map((item) => ({
                                    id: String(item.id),
                                    value: item.name!,
                                  })) || []
                                }
                                local={false}
                                handleSearch={handleCustomerSearch}
                                handleSelect={(selected) => {
                                  setSearchText("");
                                  const selectedCustomer =
                                    customerStore.customers.find(
                                      (item) =>
                                        String(item.id) ===
                                        String(selected?.[0]?.id),
                                    );
                                  orderStore.selectCustomer(
                                    selected,
                                    selectedCustomer,
                                  );
                                  if (orderStore.orderRequest.customer_id) {
                                    fetchBillFromAddress(
                                      Number(orderStore.orderRequest.customer_id),
                                    );
                                  }
                                }}
                                disabled={
                                  (userStore.hasPermission(
                                    EmployeeRoles.EDIT_ORDER,
                                  ) ||
                                    userStore.hasPermission(
                                      EmployeeRoles.CREATE_ORDER,
                                    )) &&
                                    userStore.hasPermission(
                                      EmployeeRoles.VIEW_ORDER,
                                    ) &&
                                    canAssignCustomer
                                    ? false
                                    : true
                                }
                                showId={true}
                                error={!!orderFieldErrors.customer}
                                borderColor={
                                  orderFieldErrors.customer
                                    ? theme.palette.error.main
                                    : undefined
                                }
                              />
                              {(() => {
                                const legalName =
                                  orderStore.orderRequest.customer?.legal_name?.trim() ||
                                  customerStore.customers
                                    .find(
                                      (item) =>
                                        String(item.id) ===
                                        String(
                                          orderStore.orderRequest.customer_id,
                                        ),
                                    )
                                    ?.legal_name?.trim();

                                return legalName ? (
                                  <Box
                                    sx={{
                                      mt: 1,
                                      fontSize: "0.7rem",
                                      color: "black",
                                    }}
                                  >
                                    Legal Name: {legalName}
                                  </Box>
                                ) : null;
                              })()}
                            </Box>

                            {/* User Name */}
                            <Box
                              mt={1}
                              mb={1}
                              display={"flex"}
                              justifyContent={"space-between"}
                              alignItems="center"
                            >
                              <Box
                                component="span"
                                sx={{
                                  fontSize: "0.72rem",
                                  color: "#94A3B8",
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                User Name *
                              </Box>
                              <AppButton
                                variant="tertiary"
                                label="Add"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  setOpenUsersPopup(true);
                                }}
                                customStyles={{
                                  backgroundColor: "transparent",
                                  margin: 0,
                                  fontSize: "0.85rem",
                                  color: theme.palette.customColors.pinkDark,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: theme.palette.customColors.pinkDark,
                                    boxShadow: "none",
                                  },
                                }}
                                disabled={
                                  userStore.hasPermission(
                                    EmployeeRoles.CREATE_USER,
                                  ) ||
                                    userStore.hasPermission(EmployeeRoles.EDIT_USER)
                                    ? false
                                    : true
                                }
                              />
                            </Box>
                            <Box ref={userFieldRef}>
                              <AppSearchDropdownMulti
                                isMultiSelect={false}
                                title="Select user"
                                value={
                                  orderStore.orderRequest.user_id
                                    ? [
                                      {
                                        id: String(
                                          orderStore.orderRequest.user_id,
                                        ),
                                        value:
                                          orderStore.orderRequest.user_name ||
                                          "",
                                      },
                                    ]
                                    : []
                                }
                                list={
                                  userStore.users.map((item) => ({
                                    id: String(item.id),
                                    value: `${item.first_name}${item.last_name ? " " + item.last_name : ""
                                      }`,
                                  })) || []
                                }
                                local={true}
                                handleSelect={(selected) => {
                                  const user = userStore.users.find(
                                    (u) => String(u.id) === selected[0]?.id,
                                  );
                                  orderStore.updateUser(user as NewUser);
                                }}
                                disabled={
                                  (userStore.hasPermission(
                                    EmployeeRoles.EDIT_ORDER,
                                  ) ||
                                    userStore.hasPermission(
                                      EmployeeRoles.CREATE_ORDER,
                                    )) &&
                                    userStore.hasPermission(
                                      EmployeeRoles.VIEW_ORDER,
                                    )
                                    ? false
                                    : true
                                }
                                showId={true}
                                error={!!orderFieldErrors.user}
                                borderColor={
                                  orderFieldErrors.user
                                    ? theme.palette.error.main
                                    : undefined
                                }
                              />
                            </Box>

                            {/* Phone / Email */}
                            <Box
                              sx={{
                                mt: 3,
                                display: "flex",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  p: 1,
                                  flex: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  backgroundColor: "#F8FAFC",
                                  border: "1px solid #E2E8F0",
                                  borderRadius: "8px",
                                }}
                              >
                                <CallIcon
                                  sx={{ fontSize: 16, color: "#94A3B8" }}
                                />
                                <CustomTypography
                                  variant="body3"
                                  sx={{ flex: 1, color: "#334155" }}
                                >
                                  {orderStore.orderRequest?.user?.phone || "-"}
                                </CustomTypography>
                                {orderStore.orderRequest?.user?.phone && (
                                  <Tooltip
                                    title="Copied!"
                                    open={copiedField === "phone"}
                                    disableHoverListener
                                    disableFocusListener
                                    disableTouchListener
                                  >
                                    <IconButton
                                      onClick={handleCopyPhone}
                                      sx={{ width: "24px", height: "24px" }}
                                      size="small"
                                    >
                                      <CopyAll
                                        sx={{
                                          fontSize: "14px",
                                          color: "#94A3B8",
                                        }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>

                              <Box
                                sx={{
                                  p: 1,
                                  flex: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  backgroundColor: "#F8FAFC",
                                  border: "1px solid #E2E8F0",
                                  borderRadius: "8px",
                                }}
                              >
                                <MailOutlineIcon
                                  sx={{ fontSize: 16, color: "#94A3B8" }}
                                />
                                <CustomTypography
                                  variant="body3"
                                  sx={{ flex: 1, color: "#334155" }}
                                >
                                  {orderStore.orderRequest?.user?.email || "-"}
                                </CustomTypography>
                                {orderStore.orderRequest?.user?.email && (
                                  <Tooltip
                                    title="Copied!"
                                    open={copiedField === "email"}
                                    disableHoverListener
                                    disableFocusListener
                                    disableTouchListener
                                  >
                                    <IconButton
                                      onClick={handleCopyEmail}
                                      sx={{ width: "24px", height: "24px" }}
                                      size="small"
                                    >
                                      <CopyAll
                                        sx={{
                                          fontSize: "14px",
                                          color: "#94A3B8",
                                        }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            {/* Order Status + Order Label */}
                            <Grid2 container spacing={3}>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: "0.72rem",
                                    color: "#94A3B8",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                  }}
                                >
                                  Order Status *
                                </Box>
                                <Box mt={1}>
                                  <AppSearchDropdownMulti
                                    isMultiSelect={false}
                                    title="Order Status"
                                    value={orderStatusOptions.filter(
                                      (o) =>
                                        o.id ===
                                        String(orderStore.orderRequest.status),
                                    )}
                                    list={orderStatusOptions}
                                    local={true}
                                    handleSelect={(selected) => {
                                      if (selected[0])
                                        orderStore.updateOrderStatus(
                                          Number(selected[0].id),
                                        );
                                    }}
                                    disabled={
                                      (userStore.hasPermission(
                                        EmployeeRoles.EDIT_ORDER,
                                      ) ||
                                        userStore.hasPermission(
                                          EmployeeRoles.CREATE_ORDER,
                                        )) &&
                                        userStore.hasPermission(
                                          EmployeeRoles.VIEW_ORDER,
                                        )
                                        ? false
                                        : true
                                    }
                                  />
                                </Box>
                              </Grid2>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Box ref={orderLabelFieldRef}>
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: "0.72rem",
                                      color: "#94A3B8",
                                      fontWeight: 700,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.04em",
                                    }}
                                  >
                                    Order Label *
                                  </Box>
                                  <Box mt={1}>
                                    <CustomTextField
                                      value={orderStore.orderRequest.label || ""}
                                      width={"100%"}
                                      placeholder=""
                                      onSearch={(label) => {
                                        orderStore.updateOrderLabel(label);
                                      }}
                                      label="Order Label"
                                      endAdornment={false}
                                      borderColor={
                                        orderFieldErrors.orderLabel
                                          ? theme.palette.error.main
                                          : undefined
                                      }
                                      disabled={
                                        (userStore.hasPermission(
                                          EmployeeRoles.EDIT_ORDER,
                                        ) ||
                                          userStore.hasPermission(
                                            EmployeeRoles.CREATE_ORDER,
                                          )) &&
                                          userStore.hasPermission(
                                            EmployeeRoles.VIEW_ORDER,
                                          )
                                          ? false
                                          : true
                                      }
                                    />
                                  </Box>
                                </Box>
                              </Grid2>
                            </Grid2>

                            {/* Order Type + Tagged Order Ids */}
                            <Grid2 container spacing={3} sx={{ mt: 1 }}>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Box ref={orderTypeFieldRef}>
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: "0.72rem",
                                      color: "#94A3B8",
                                      fontWeight: 700,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.04em",
                                    }}
                                  >
                                    Order Type *
                                  </Box>
                                  <Box mt={1}>
                                    <AppSearchDropdownMulti
                                      isMultiSelect={false}
                                      title="Order Type"
                                      value={projectStore.orderType.filter(
                                        (o) =>
                                          o.id ===
                                          String(orderStore.orderRequest.type_id),
                                      )}
                                      list={projectStore.orderType || []}
                                      local={true}
                                      handleSelect={(selected) => {
                                        if (selected[0])
                                          orderStore.updateOrderType(
                                            Number(selected[0].id),
                                          );
                                      }}
                                      error={!!orderFieldErrors.orderType}
                                      borderColor={
                                        orderFieldErrors.orderType
                                          ? theme.palette.error.main
                                          : undefined
                                      }
                                      disabled={
                                        (userStore.hasPermission(
                                          EmployeeRoles.EDIT_ORDER,
                                        ) ||
                                          userStore.hasPermission(
                                            EmployeeRoles.CREATE_ORDER,
                                          )) &&
                                          userStore.hasPermission(
                                            EmployeeRoles.VIEW_ORDER,
                                          )
                                          ? false
                                          : true
                                      }
                                    />
                                  </Box>
                                </Box>
                              </Grid2>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: "0.72rem",
                                    color: "#94A3B8",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                  }}
                                >
                                  Tagged Order Ids
                                </Box>
                                <Box mt={1}>
                                  <AppSearchDropdownMulti
                                    isMultiSelect={true}
                                    title="Search order Id"
                                    list={
                                      orderStore?.orderIds?.map((orderId) => ({
                                        id: orderId.toString(),
                                        value: orderId.toString(),
                                      })) || []
                                    }
                                    local={false}
                                    handleSearch={(orderId) =>
                                      handleOrderIdSearch(orderId)
                                    }
                                    handleSelect={(selectedOrders) =>
                                      orderStore.updateTaggedOrderIds(
                                        selectedOrders,
                                      )
                                    }
                                    value={
                                      orderStore.orderRequest.tagged_order_ids
                                        ? orderStore.orderRequest.tagged_order_ids
                                          .split(",")
                                          .map((id) => ({ id, value: id }))
                                        : []
                                    }
                                  />
                                </Box>
                              </Grid2>
                            </Grid2>

                            {/* Client PO Number */}
                            <Grid2 container spacing={3} sx={{ mt: 1 }}>
                              <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: "0.72rem",
                                    color: "#94A3B8",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                  }}
                                >
                                  Client PO Number
                                </Box>
                                <Box
                                  mt={1}
                                  display={"flex"}
                                  alignItems={"center"}
                                  gap={1}
                                >
                                  <CustomTextField
                                    width={"100%"}
                                    value={
                                      orderStore.orderRequest.client_po_number ||
                                      ""
                                    }
                                    placeholder=""
                                    onSearch={(poNumber) => {
                                      orderStore.updateClientPONumber(poNumber);
                                    }}
                                    label="Client PO Number"
                                    disabled={true}
                                  />
                                  {orderStore.orderRequest?.client_po_url && (
                                    <IconButton
                                      onClick={() => {
                                        orderStore
                                          .downloadClientPODocument()
                                          .then((response) => {
                                            if (response.data) {
                                              const blob = new Blob(
                                                [response.data],
                                                { type: "application/pdf" },
                                              );
                                              const downloadUrl =
                                                window.URL.createObjectURL(blob);
                                              const a =
                                                document.createElement("a");
                                              a.href = downloadUrl;
                                              if (
                                                orderStore.orderRequest
                                                  .client_po_number
                                              ) {
                                                a.download =
                                                  orderStore.orderRequest.client_po_number.replace(
                                                    / /g,
                                                    "_",
                                                  ) + ".pdf";
                                              } else {
                                                a.download =
                                                  response.filename ||
                                                  "Client PO.pdf";
                                              }
                                              document.body.appendChild(a);
                                              a.click();
                                              a.remove();
                                              window.URL.revokeObjectURL(
                                                downloadUrl,
                                              );
                                            } else {
                                              showWarning(
                                                "Failed to download Client PO document.",
                                              );
                                            }
                                          })
                                          .catch((err) => {
                                            const errorMessage =
                                              err?.response?.data?.message ||
                                              err.message;
                                            showError(
                                              "Error Occurred",
                                              errorMessage,
                                            );
                                          });
                                      }}
                                      sx={{
                                        color: theme.palette.primary.main,
                                        backgroundColor:
                                          theme.palette.primary.main + "10",
                                        "&:hover": {
                                          backgroundColor:
                                            theme.palette.primary.main + "20",
                                          transform: "scale(1.05)",
                                        },
                                        transition: "all 0.2s ease",
                                      }}
                                      size="small"
                                    >
                                      <DownloadIcon />
                                    </IconButton>
                                  )}
                                </Box>
                              </Grid2>
                              <Grid2 size={{ xs: 12, sm: 6 }} />
                            </Grid2>
                          </CardContent>
                        </Card>
                      </Grid2>

                      {/* Upload Logos Card */}
                      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                        <Card
                          sx={{
                            height: "100%",
                            boxShadow:
                              "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {/* Header */}
                          <Box
                            sx={{
                              px: 3,
                              pt: 2.5,
                              pb: 2,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <CustomTypography variant="heading4">
                                Upload Logos
                              </CustomTypography>
                              {orderStore.orderArtworks.length > 0 && (
                                <Chip
                                  size="small"
                                  label={`${orderStore.orderArtworks.length}`}
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    bgcolor: theme.palette.primary.main + "15",
                                    color: theme.palette.primary.main,
                                    border: `1px solid ${theme.palette.primary.main}30`,
                                  }}
                                />
                              )}
                            </Box>
                            {orderId && (
                              <Box display="flex" alignItems="center" gap={1}>
                                <input
                                  ref={logoUploadInputRef}
                                  type="file"
                                  multiple
                                  hidden
                                  accept="image/*,.ai,.eps,.cdr,.dst,.emb"
                                  onChange={handleLogoFilesSelected}
                                  disabled={
                                    !canManageLogos || isLogoUploadLoading
                                  }
                                />
                                <Tooltip title="Refresh logos">
                                  <span>
                                    <IconButton
                                      onClick={refreshOrderLogos}
                                      disabled={orderStore.isLogoLoading}
                                      size="small"
                                      sx={{
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        height: 32,
                                        width: 32,
                                        "&:hover": {
                                          borderColor: theme.palette.primary.main,
                                          bgcolor:
                                            theme.palette.primary.main + "08",
                                        },
                                      }}
                                    >
                                      {orderStore.isLogoLoading ? (
                                        <CircularProgress size={14} />
                                      ) : (
                                        <RefreshIcon
                                          sx={{
                                            fontSize: "1rem",
                                            color:
                                              theme.palette.customColors.pinkDark,
                                          }}
                                        />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <AppButton
                                  label={
                                    isLogoUploadLoading
                                      ? "Uploading..."
                                      : "Upload Files"
                                  }
                                  onClick={handleLogoUploadClick}
                                  disabled={
                                    !canManageLogos || isLogoUploadLoading
                                  }
                                  customStyles={{
                                    fontSize: "0.78rem",
                                    height: "32px",
                                    minWidth: "110px",
                                  }}
                                />
                              </Box>
                            )}
                          </Box>

                          {/* Body */}
                          <Box
                            sx={{
                              px: 3,
                              py: 2.5,
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color={theme.palette.customColors.greyTextColor}
                              sx={{ mb: 2 }}
                            >
                              Accepted: image, AI, EPS, CDR. View or download
                              anytime while uploads continue.
                            </CustomTypography>

                            {/* File area */}
                            <Box
                              sx={{
                                flex: 1,
                                border: `1.5px dashed ${theme.palette.divider}`,
                                borderRadius: 2,
                                height: "100%",
                                overflowY: "auto",
                                bgcolor: theme.palette.grey[50],
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {orderStore.isLogoLoading ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  flex={1}
                                  minHeight={200}
                                >
                                  <CircularProgress size={28} />
                                </Box>
                              ) : orderStore.orderArtworks.length === 0 ? (
                                /* Empty state */
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  justifyContent="center"
                                  flex={1}
                                  minHeight={200}
                                  gap={1}
                                  onClick={
                                    canManageLogos && !isLogoUploadLoading
                                      ? handleLogoUploadClick
                                      : undefined
                                  }
                                  sx={{
                                    cursor:
                                      canManageLogos && !isLogoUploadLoading
                                        ? "pointer"
                                        : "default",
                                    transition: "background 0.15s",
                                    borderRadius: 2,
                                    "&:hover":
                                      canManageLogos && !isLogoUploadLoading
                                        ? {
                                          bgcolor:
                                            theme.palette.primary.main + "06",
                                        }
                                        : {},
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: "50%",
                                      bgcolor: theme.palette.primary.main + "12",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <InsertDriveFileOutlinedIcon
                                      sx={{
                                        color: theme.palette.primary.main,
                                        fontSize: "1.4rem",
                                      }}
                                    />
                                  </Box>
                                  <CustomTypography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    No logos uploaded yet
                                  </CustomTypography>
                                  {canManageLogos && (
                                    <CustomTypography
                                      variant="body2"
                                      color={theme.palette.primary.main}
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      Click to upload files
                                    </CustomTypography>
                                  )}
                                </Box>
                              ) : (
                                /* File grid */
                                <Box sx={{ p: 1.5 }}>
                                  <Grid2 container spacing={1.5}>
                                    {orderStore.orderArtworks.map(
                                      (logo, index) => {
                                        const logoUrl = getLogoUrl(logo);
                                        const logoName = getLogoName(logo, index);
                                        const showImagePreview =
                                          isImageLogo(logo);

                                        return (
                                          <Grid2
                                            size={{ xs: 6, sm: 4, md: 4 }}
                                            key={`${logoName}-${index}`}
                                          >
                                            <Box
                                              sx={{
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 1.5,
                                                overflow: "hidden",
                                                bgcolor:
                                                  theme.palette.background.paper,
                                                transition:
                                                  "box-shadow 0.15s, border-color 0.15s",
                                                "&:hover": {
                                                  boxShadow:
                                                    "0 2px 10px rgba(0,0,0,0.10)",
                                                  borderColor:
                                                    theme.palette.primary.main +
                                                    "60",
                                                  "& .logo-actions": {
                                                    opacity: 1,
                                                  },
                                                },
                                                position: "relative",
                                              }}
                                            >
                                              {/* Preview area */}
                                              <Box
                                                sx={{
                                                  height: 90,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  bgcolor:
                                                    theme.palette.grey[100],
                                                  overflow: "hidden",
                                                  position: "relative",
                                                }}
                                              >
                                                {showImagePreview ? (
                                                  <Box
                                                    component="img"
                                                    src={logoUrl}
                                                    alt={logoName}
                                                    sx={{
                                                      maxHeight: "100%",
                                                      maxWidth: "100%",
                                                      objectFit: "contain",
                                                      p: 1,
                                                    }}
                                                  />
                                                ) : (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      alignItems: "center",
                                                      gap: 0.5,
                                                    }}
                                                  >
                                                    <InsertDriveFileOutlinedIcon
                                                      sx={{
                                                        color:
                                                          theme.palette.text
                                                            .disabled,
                                                        fontSize: "1.8rem",
                                                      }}
                                                    />
                                                    <CustomTypography
                                                      variant="body2"
                                                      sx={{
                                                        fontSize: "0.6rem",
                                                        fontWeight: 700,
                                                        color:
                                                          theme.palette.text
                                                            .disabled,
                                                        textTransform:
                                                          "uppercase",
                                                        letterSpacing: 0.5,
                                                      }}
                                                    >
                                                      {logoName
                                                        .split(".")
                                                        .pop() || "FILE"}
                                                    </CustomTypography>
                                                  </Box>
                                                )}

                                                {/* Hover action overlay */}
                                                <Box
                                                  className="logo-actions"
                                                  sx={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    bgcolor: "rgba(0,0,0,0.45)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 0.5,
                                                    opacity: 0,
                                                    transition: "opacity 0.15s",
                                                  }}
                                                >
                                                  <Tooltip title="View">
                                                    <span>
                                                      <IconButton
                                                        size="small"
                                                        disabled={!logoUrl}
                                                        onClick={() =>
                                                          window.open(
                                                            logoUrl,
                                                            "_blank",
                                                          )
                                                        }
                                                        sx={{
                                                          bgcolor:
                                                            "rgba(255,255,255,0.15)",
                                                          color: "#fff",
                                                          "&:hover": {
                                                            bgcolor:
                                                              "rgba(255,255,255,0.30)",
                                                          },
                                                          width: 30,
                                                          height: 30,
                                                        }}
                                                      >
                                                        <VisibilityOutlinedIcon
                                                          sx={{
                                                            fontSize: "0.95rem",
                                                          }}
                                                        />
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>
                                                  <Tooltip title="Download">
                                                    <span>
                                                      <IconButton
                                                        size="small"
                                                        disabled={!logoUrl}
                                                        onClick={() =>
                                                          handleDownloadLogo(
                                                            logoUrl,
                                                            logoName,
                                                          )
                                                        }
                                                        sx={{
                                                          bgcolor:
                                                            "rgba(255,255,255,0.15)",
                                                          color: "#fff",
                                                          "&:hover": {
                                                            bgcolor:
                                                              "rgba(255,255,255,0.30)",
                                                          },
                                                          width: 30,
                                                          height: 30,
                                                        }}
                                                      >
                                                        <DownloadIcon
                                                          sx={{
                                                            fontSize: "0.95rem",
                                                          }}
                                                        />
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>
                                                </Box>
                                              </Box>

                                              {/* Filename */}
                                              <Box sx={{ px: 1, py: 0.8 }}>
                                                <Tooltip title={logoName}>
                                                  <CustomTypography
                                                    variant="body2"
                                                    sx={{
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap",
                                                      fontSize: "0.7rem",
                                                      color:
                                                        theme.palette.text
                                                          .secondary,
                                                    }}
                                                  >
                                                    {logoName}
                                                  </CustomTypography>
                                                </Tooltip>
                                              </Box>
                                            </Box>
                                          </Grid2>
                                        );
                                      },
                                    )}
                                  </Grid2>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Card>
                      </Grid2>
                    </Grid2>

                    {/* Row 2: Address Cards - Billing, Shipping, Bill From */}
                    <Grid2 container spacing={3}>
                      {/* Billing Address Card */}
                      <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                        <Card
                          sx={{
                            height: "100%",
                            p: 3,
                            boxShadow:
                              "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 0,
                              "&:last-child": { pb: 0 },
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              marginTop="-5px"
                            >
                              <CustomTypography variant="heading4">
                                Billing Address
                              </CustomTypography>
                              <AppButton
                                label="Add"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  handleOpenEditAddressPopup("addBilling")
                                }
                                customStyles={{
                                  backgroundColor: "transparent",
                                  padding: 1,
                                  margin: 0,
                                  fontSize: "0.9rem",
                                  color: theme.palette.customColors.pinkDark,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: theme.palette.customColors.pinkDark,
                                    boxShadow: "none",
                                  },
                                }}
                                disabled={
                                  userStore.hasPermission(
                                    EmployeeRoles.CREATE_ORDER,
                                  ) ||
                                    userStore.hasPermission(
                                      EmployeeRoles.EDIT_ORDER,
                                    )
                                    ? false
                                    : true
                                }
                              />
                            </Box>

                            <Box
                              mt={2}
                              padding={2}
                              borderRadius={"10px"}
                              border={"1px solid #E2E8F0"}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                maxHeight: "220px",
                                minHeight: "220px",
                                overflowY: "auto",
                                backgroundColor: "#F8FAFC",
                              }}
                            >
                              <Box
                                sx={{
                                  flexGrow: 1,
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {(() => {
                                  const { billing_address } =
                                    orderStore.orderRequest;

                                  if (!billing_address) {
                                    return (
                                      !isLoading && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minHeight: "100%",
                                          }}
                                        >
                                          <CustomTypography
                                            variant="body1"
                                            color={
                                              theme.palette.customColors
                                                .greyTextColor
                                            }
                                          >
                                            No billing address selected yet.
                                          </CustomTypography>
                                        </Box>
                                      )
                                    );
                                  }

                                  return (
                                    <>
                                      {(billing_address.first_name ||
                                        billing_address.last_name) && (
                                          <CustomTypography
                                            variant="body2"
                                            fontWeight={700}
                                          >
                                            {`${billing_address.first_name || ""} ${billing_address.last_name || ""
                                              }`.trim()}
                                          </CustomTypography>
                                        )}

                                      {(billing_address.address1 ||
                                        billing_address.address2 ||
                                        billing_address.address3) && (
                                          <CustomTypography
                                            variant="body3"
                                            style={{ marginTop: 5 }}
                                            color="#7C7A7F"
                                          >
                                            {renderAddress(
                                              billing_address.address1,
                                              billing_address.address2,
                                              billing_address.address3,
                                            )}
                                          </CustomTypography>
                                        )}

                                      {(billing_address.city_name ||
                                        billing_address.state_name) && (
                                          <CustomTypography
                                            variant="body3"
                                            color="#7C7A7F"
                                          >
                                            {renderAddress(
                                              billing_address.city_name,
                                              billing_address.state_name,
                                            )}
                                          </CustomTypography>
                                        )}

                                      {(billing_address.country_name ||
                                        billing_address.postal_code) && (
                                          <CustomTypography
                                            variant="body3"
                                            color="#7C7A7F"
                                          >
                                            {renderAddress(
                                              billing_address.country_name,
                                              billing_address.postal_code,
                                            )}
                                          </CustomTypography>
                                        )}

                                      {billing_address.contact_no && (
                                        <CustomTypography
                                          variant="body3"
                                          color="#7C7A7F"
                                        >
                                          Contact No: {billing_address.contact_no}
                                        </CustomTypography>
                                      )}

                                      {billing_address.gstin && (
                                        <CustomTypography
                                          variant="body3"
                                          color="#7C7A7F"
                                        >
                                          GSTIN: {billing_address.gstin}
                                        </CustomTypography>
                                      )}

                                      {billing_address.email && (
                                        <CustomTypography
                                          variant="body3"
                                          color="#7C7A7F"
                                        >
                                          Email: {billing_address.email}
                                        </CustomTypography>
                                      )}
                                    </>
                                  );
                                })()}
                              </Box>

                              <Box mt={2} mb={1} gap={3} display={"flex"}>
                                {orderStore.orderRequest.billing_address && (
                                  <AppButton
                                    customStyles={{ width: "85px" }}
                                    label="Edit"
                                    onClick={() =>
                                      handleOpenEditAddressPopup("billing")
                                    }
                                    disabled={
                                      (userStore.hasPermission(
                                        EmployeeRoles.EDIT_ORDER,
                                      ) ||
                                        userStore.hasPermission(
                                          EmployeeRoles.CREATE_ORDER,
                                        )) &&
                                        userStore.hasPermission(
                                          EmployeeRoles.VIEW_ORDER,
                                        )
                                        ? false
                                        : true
                                    }
                                  />
                                )}
                                <AppButton
                                  customStyles={{ width: "85px" }}
                                  variant="grayscale"
                                  label={
                                    isSelectAddressLoading
                                      ? "Loading..."
                                      : "Select"
                                  }
                                  onClick={() =>
                                    handleSelectClick("billingAddress")
                                  }
                                  startIcon={
                                    isSelectAddressLoading ? null : SelectAllIcon
                                  }
                                  disabled={
                                    ((userStore.hasPermission(
                                      EmployeeRoles.EDIT_ORDER,
                                    ) ||
                                      userStore.hasPermission(
                                        EmployeeRoles.CREATE_ORDER,
                                      )) &&
                                      userStore.hasPermission(
                                        EmployeeRoles.VIEW_ORDER,
                                      )) ||
                                      isSelectAddressLoading
                                      ? false
                                      : true
                                  }
                                />
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid2>

                      {/* Shipping Address Card */}
                      <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                        <Card
                          sx={{
                            height: "100%",
                            p: 3,
                            boxShadow:
                              "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 0,
                              "&:last-child": { pb: 0 },
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              marginTop="-5px"
                            >
                              <CustomTypography variant="heading4">
                                Shipping Address
                              </CustomTypography>
                              <AppButton
                                label="Add"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  handleOpenEditAddressPopup("addShipping")
                                }
                                disabled={
                                  userStore.hasPermission(
                                    EmployeeRoles.CREATE_ORDER,
                                  ) ||
                                    userStore.hasPermission(
                                      EmployeeRoles.EDIT_ORDER,
                                    )
                                    ? false
                                    : true
                                }
                                customStyles={{
                                  backgroundColor: "transparent",
                                  padding: 1,
                                  margin: 0,
                                  fontSize: "0.9rem",
                                  color: theme.palette.customColors.pinkDark,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: theme.palette.customColors.pinkDark,
                                    boxShadow: "none",
                                  },
                                }}
                              />
                            </Box>

                            <Box
                              mt={2}
                              padding={2}
                              borderRadius={"10px"}
                              border={"1px solid #E2E8F0"}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                maxHeight: "220px",
                                minHeight: "220px",
                                overflowY: "auto",
                                backgroundColor: "#F8FAFC",
                              }}
                            >
                              <Box
                                sx={{
                                  flexGrow: 1,
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {(() => {
                                  const { shipping_address } =
                                    orderStore.orderRequest;
                                  if (!shipping_address) {
                                    return (
                                      !isLoading && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minHeight: "100%",
                                          }}
                                        >
                                          <CustomTypography
                                            variant="body1"
                                            color={
                                              theme.palette.customColors
                                                .greyTextColor
                                            }
                                          >
                                            No shipping address selected yet.
                                          </CustomTypography>
                                        </Box>
                                      )
                                    );
                                  }

                                  return (
                                    <>
                                      {(shipping_address.first_name ||
                                        shipping_address.last_name) && (
                                          <CustomTypography
                                            variant="body2"
                                            fontWeight={700}
                                          >
                                            {`${shipping_address.first_name || ""} ${shipping_address.last_name || ""
                                              }`.trim()}
                                          </CustomTypography>
                                        )}

                                      {(shipping_address.address1 ||
                                        shipping_address.address2 ||
                                        shipping_address.address3) && (
                                          <CustomTypography
                                            variant="body3"
                                            style={{ marginTop: 5 }}
                                            color="#7C7A7F"
                                          >
                                            {renderAddress(
                                              shipping_address.address1,
                                              shipping_address.address2,
                                              shipping_address.address3,
                                            )}
                                          </CustomTypography>
                                        )}

                                      {(shipping_address.city_name ||
                                        shipping_address.state_name) && (
                                          <CustomTypography
                                            variant="body3"
                                            color="#7C7A7F"
                                          >
                                            {renderAddress(
                                              shipping_address.city_name,
                                              shipping_address.state_name,
                                            )}
                                          </CustomTypography>
                                        )}

                                      {(shipping_address.country_name ||
                                        shipping_address.postal_code) && (
                                          <CustomTypography
                                            variant="body3"
                                            color="#7C7A7F"
                                          >
                                            {renderAddress(
                                              shipping_address.country_name,
                                              shipping_address.postal_code,
                                            )}
                                          </CustomTypography>
                                        )}

                                      {shipping_address.contact_no && (
                                        <CustomTypography
                                          variant="body3"
                                          color="#7C7A7F"
                                        >
                                          Contact No:{" "}
                                          {shipping_address.contact_no}
                                        </CustomTypography>
                                      )}

                                      {shipping_address.gstin && (
                                        <CustomTypography
                                          variant="body3"
                                          color="#7C7A7F"
                                        >
                                          GSTIN: {shipping_address.gstin}
                                        </CustomTypography>
                                      )}

                                      {shipping_address.email && (
                                        <CustomTypography
                                          variant="body3"
                                          color="#7C7A7F"
                                        >
                                          Email: {shipping_address.email}
                                        </CustomTypography>
                                      )}
                                    </>
                                  );
                                })()}
                              </Box>

                              <Box mt={2} mb={1} gap={3} display={"flex"}>
                                {orderStore.orderRequest.shipping_address && (
                                  <AppButton
                                    customStyles={{ width: "85px" }}
                                    label="Edit"
                                    onClick={() =>
                                      handleOpenEditAddressPopup("shipping")
                                    }
                                    disabled={
                                      userStore.hasPermission(
                                        EmployeeRoles.CREATE_ORDER,
                                      ) ||
                                        userStore.hasPermission(
                                          EmployeeRoles.EDIT_ORDER,
                                        )
                                        ? false
                                        : true
                                    }
                                  />
                                )}
                                <AppButton
                                  customStyles={{ width: "85px" }}
                                  variant="grayscale"
                                  label={
                                    isSelectShippingAddressLoading
                                      ? "Loading..."
                                      : "Select"
                                  }
                                  disabled={
                                    ((userStore.hasPermission(
                                      EmployeeRoles.CREATE_ORDER,
                                    ) ||
                                      userStore.hasPermission(
                                        EmployeeRoles.EDIT_ORDER,
                                      )) &&
                                      userStore.hasPermission(
                                        EmployeeRoles.VIEW_ORDER,
                                      )) ||
                                      isSelectShippingAddressLoading
                                      ? false
                                      : true
                                  }
                                  onClick={() =>
                                    handleSelectClick("shippingAddress")
                                  }
                                  startIcon={
                                    isSelectShippingAddressLoading
                                      ? null
                                      : SelectAllIcon
                                  }
                                />
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid2>

                      {/* Bill From Address Card */}
                      <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                        <Card
                          sx={{
                            height: "100%",
                            p: 3,
                            boxShadow:
                              "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 0,
                              "&:last-child": { pb: 0 },
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              marginTop="-5px"
                            >
                              <CustomTypography variant="heading4">
                                Bill From Address
                              </CustomTypography>
                            </Box>

                            <Box
                              mt={2}
                              padding={2}
                              borderRadius={"10px"}
                              border={"1px solid #E2E8F0"}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                maxHeight: "240px",
                                minHeight: "240px",
                                overflowY: "auto",
                                backgroundColor: "#F8FAFC",
                              }}
                            >
                              <Box
                                sx={{
                                  flexGrow: 1,
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {(() => {
                                  const { bill_from_address } =
                                    orderStore.orderRequest;
                                  if (!bill_from_address) {
                                    return (
                                      !isLoading && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minHeight: "100%",
                                          }}
                                        >
                                          <CustomTypography
                                            variant="body1"
                                            color={
                                              theme.palette.customColors
                                                .greyTextColor
                                            }
                                          >
                                            No bill from address selected yet.
                                          </CustomTypography>
                                        </Box>
                                      )
                                    );
                                  }

                                  return (
                                    <>
                                      {(bill_from_address.first_name ||
                                        bill_from_address.last_name) && (
                                          <CustomTypography
                                            variant="body2"
                                            fontWeight={700}
                                          >
                                            {`${bill_from_address.first_name || ""} ${bill_from_address.last_name || ""
                                              }`.trim()}
                                          </CustomTypography>
                                        )}

                                      {(bill_from_address.address1 ||
                                        bill_from_address.address2 ||
                                        bill_from_address.address3) && (
                                          <CustomTypography
                                            variant="body3"
                                            style={{ marginTop: 5 }}
                                            color="#7C7A7F"
                                          >
                                            {renderAddress(
                                              bill_from_address.address1,
                                              bill_from_address.address2,
                                              bill_from_address.address3,
                                            )}
                                          </CustomTypography>
                                        )}

                                      {(bill_from_address.city_name ||
                                        bill_from_address.state_name) && (
                                          <CustomTypography
                                            variant="body3"
                                            color="#7C7A7F"
                                          >
                                            {renderAddress(
                                              bill_from_address.city_name,
                                              bill_from_address.state_name,
                                            )}
                                          </CustomTypography>
                                        )}

                                      {(bill_from_address.country_name ||
                                        bill_from_address.postal_code) && (
                                          <CustomTypography
                                            variant="body3"
                                            color="#7C7A7F"
                                          >
                                            {renderAddress(
                                              bill_from_address.country_name,
                                              bill_from_address.postal_code,
                                            )}
                                          </CustomTypography>
                                        )}

                                      {bill_from_address.contact_no && (
                                        <CustomTypography
                                          variant="body3"
                                          color="#7C7A7F"
                                        >
                                          Contact No:{" "}
                                          {bill_from_address.contact_no}
                                        </CustomTypography>
                                      )}

                                      {bill_from_address.gstin && (
                                        <CustomTypography
                                          variant="body3"
                                          color="#7C7A7F"
                                        >
                                          GSTIN: {bill_from_address.gstin}
                                        </CustomTypography>
                                      )}

                                      {bill_from_address.email && (
                                        <CustomTypography
                                          variant="body3"
                                          color="#7C7A7F"
                                        >
                                          Email: {bill_from_address.email}
                                        </CustomTypography>
                                      )}
                                    </>
                                  );
                                })()}
                              </Box>

                              <Box mt={2} mb={1} gap={3} display={"flex"}>
                                <AppButton
                                  customStyles={{ width: "85px" }}
                                  variant="grayscale"
                                  label={
                                    isSelectShippingAddressLoading
                                      ? "Loading..."
                                      : "Select"
                                  }
                                  disabled={
                                    userStore.hasPermission(
                                      EmployeeRoles.EDIT_INVOICE,
                                    ) ||
                                      userStore.hasPermission(
                                        EmployeeRoles.CREATE_INVOICE_AND_PI,
                                      ) ||
                                      isSelectShippingAddressLoading
                                      ? false
                                      : true
                                  }
                                  onClick={() => handleSelectClick("billFrom")}
                                  startIcon={
                                    isSelectShippingAddressLoading
                                      ? null
                                      : SelectAllIcon
                                  }
                                />
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid2>
                    </Grid2>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ paddingTop: 4 }} ref={additionalInfoSectionRef}>
                {/* Additional Info */}
                <Card
                  sx={{
                    flexBasis: "30%",
                    flexGrow: 1,
                    p: 2,
                    boxShadow:
                      "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "310px",
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                    }}
                  >
                    <CustomTypography
                      variant="heading4"
                      fontWeight={700}
                      sx={{ marginBottom: "16px" }}
                    >
                      Additional Info
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginLeft: "8px",
                        }}
                      >
                        (Max: 500 characters)
                      </span>
                    </CustomTypography>
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        mt: 2,
                        minHeight: "150px",
                        maxHeight: "150px",
                        alignItems: "start",
                      }}
                    >
                      <FormTextField
                        id="additionalInfo"
                        multiline
                        maxLength={ADDITIONAL_INFO_MAX_LENGTH}
                        error={orderFieldErrors.additionalInfo}
                        errorMessage={`Maximum ${ADDITIONAL_INFO_MAX_LENGTH} characters allowed.`}
                        sx={{
                          flexGrow: 1,
                          marginBottom: "0px",
                          "& .MuiInputBase-root": {
                            height: "150px",
                            alignItems: "start",
                            overflow: "auto",
                          },
                          "& .MuiInputBase-input": {
                            maxHeight: "170px",
                            overflowY: "auto",
                            display: "block",
                          },
                        }}
                        placeholder="Add additional information"
                        value={orderStore.orderRequest.comments ?? ""}
                        onChange={(id, e) => {
                          const next = e ?? "";
                          orderStore.updateAdditionalInfo(next);
                          if (
                            orderFieldErrors.additionalInfo &&
                            next.length <= ADDITIONAL_INFO_MAX_LENGTH
                          ) {
                            setOrderFieldErrors((prev) => ({
                              ...prev,
                              additionalInfo: false,
                            }));
                          }
                        }}
                        disabled={
                          (userStore.hasPermission(EmployeeRoles.EDIT_ORDER) ||
                            userStore.hasPermission(
                              EmployeeRoles.CREATE_ORDER,
                            )) &&
                            userStore.hasPermission(EmployeeRoles.VIEW_ORDER)
                            ? false
                            : true
                        }
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {(userStore.hasPermission(EmployeeRoles.CREATE_ORDER) ||
                userStore.hasPermission(EmployeeRoles.EDIT_ORDER)) &&
                orderId && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                      gap: 2,
                      flexDirection: { xs: "column", md: "row" },
                    }}
                  >
                    <AppButton
                      variant="primary"
                      label="Save Basic Details"
                      onClick={() => CreateOrUpdateOrder()}
                      customStyles={{ minWidth: "120px" }}
                    />
                  </Box>
                )}
              <Box ref={orderLineItemsSectionRef}>
                <Box mt={4}>
                  <OrderProducts
                    validationErrorBackground={
                      orderFieldErrors.productsOrServices
                    }
                    order_id={orderId ? Number(orderId) : undefined}
                    products={orderStore.orderRequest.order_products || []}
                    profit_loss_statement={
                      orderStore.existingOrder?.profit_loss_statement
                    }
                    canGiveDiscount={orderId !== null}
                    canUpdateOfferPrice={orderId !== null}
                    canUpdateSellingPrice={orderId !== null}
                    handleDeleteProduct={(id: number) => {
                      handleDeleteProduct(id);
                    }}
                    updateQuantity={(
                      id: number,
                      value: number,
                      field: string,
                    ) => {
                      handleUpdateProductDetails(id, value, field);
                    }}
                    toggleAddProductsPopup={() =>
                      setOpenAddProductsPopup(!openAddProductsPopup)
                    }
                    handleViewImagesOfProduct={(product: OrderProduct) => {
                      setMockupProductDetails({
                        id: product.id,
                        name: product.name!,
                        image_url: product.image_url,
                      });
                      setOpenImageGalleryModal(true);
                    }}
                    handleViewMockupOfProduct={(product: OrderProduct) => {
                      setMockupProductDetails({
                        id: product.id,
                        name: product.name!,
                        image_url: product.image_url,
                      });
                      handleViewMockupOfProduct(product.id);
                    }}
                    handleUpdateAllBtnClick={() =>
                      setOpenUpdateAllQuantitiesPopup(
                        !openUpdateAllQuantitiesPopup,
                      )
                    }
                    isSez={Boolean(orderStore.orderRequest?.is_sez)}
                  />
                </Box>

                <Box mt={4}>
                  <OrderServiceTable
                    validationErrorBackground={
                      orderFieldErrors.productsOrServices
                    }
                    services={orderStore.orderRequest.order_services || []}
                    handleDeleteService={(id: number, custom_name: string) => {
                      handleDeleteService(id, custom_name);
                    }}
                    updateQuantity={(
                      id: number,
                      custom_name: string,
                      value: number,
                      field: string,
                    ) => {
                      handleUpdateServiccesDetails(id, custom_name, value, field);
                    }}
                    toggleServicesPopup={() =>
                      setOpenopenServicesPopus(!openServicesPopus)
                    }
                    isSez={Boolean(orderStore.orderRequest?.is_sez)}
                  />
                </Box>
              </Box>

              <Box mt={4}>
                {orderId &&
                  stockStore.orderStockCheckReqDetails &&
                  stockStore.orderStockCheckReqDetails.length > 0 && (
                    <StockCheckRequestTable
                      lists={stockStore.orderStockCheckReqDetails}
                      onEditClick={(requestId, product) => {
                        setSelectedProduct(product);
                        setSelectedStockCheckRequestId(requestId);
                        setOpenRaiseStockCheckDetailsPopup(
                          !openRaiseStockCheckDetailsPopup,
                        );
                      }}
                      onStatusChange={(requestId: number, status: number) => {
                        stockStore.updateStatus(requestId, status);
                      }}
                      saveStatus={(requestId) =>
                        updateStockCheckRequestStatus(requestId)
                      }
                      onDeleteSuggestedProduct={deleteSuggestedProduct}
                    />
                  )}
              </Box>

              <Box mt={4}>
                {orderId &&
                  ((orderStore.orderRequest.order_products &&
                    orderStore.orderRequest.order_products?.length > 0) ||
                    (orderStore.orderRequest.order_services &&
                      orderStore.orderRequest.order_services?.length > 0)) && (
                    <InvoiceListingTable
                      invoices={orderStore.orderRequest.invoices || []}
                      handleDeleteInvoice={(invoice) => {
                        setConfirmationPopupData({
                          isOpen: true,
                          invoice,
                          mode: "cancel",
                        });
                      }}
                      handleReactivateInvoice={(invoice) => {
                        setConfirmationPopupData({
                          isOpen: true,
                          invoice,
                          mode: "reactivate",
                        });
                      }}
                      handleDownloadOldInvoice={(invoice) => {
                        handleDownloadOldInvoice(invoice);
                      }}
                      handleDownloadInvoice={(invoice) =>
                        downloadInvoice(invoice)
                      }
                    />
                  )}

                {
                  <>
                    <SingleInput
                      key={`${confirmationPopupData.mode}-${confirmationPopupData.invoice?.id ?? ""}`}
                      required={confirmationPopupData.mode === "cancel"}
                      allowEmptySubmit={
                        confirmationPopupData.mode === "reactivate"
                      }
                      multiline={true}
                      buttonMarginTop={0}
                      open={confirmationPopupData.isOpen}
                      type="text"
                      heading={
                        confirmationPopupData.mode === "cancel"
                          ? "Cancel Invoice"
                          : "Reactivate Invoice"
                      }
                      content={
                        confirmationPopupData.mode === "cancel"
                          ? "Please provide reason for cancellation (mandatory)"
                          : "Reason (optional)"
                      }
                      onSubmit={(reason) => {
                        if (confirmationPopupData.invoice) {
                          if (confirmationPopupData.mode === "cancel") {
                            deleteInvoice(confirmationPopupData.invoice, reason);
                          } else {
                            reactivateInvoice(
                              confirmationPopupData.invoice,
                              reason,
                            );
                          }
                        }
                        setConfirmationPopupData({
                          isOpen: false,
                          invoice: null,
                          mode: "cancel",
                        });
                      }}
                      onClose={() =>
                        setConfirmationPopupData({
                          isOpen: false,
                          invoice: null,
                          mode: "cancel",
                        })
                      }
                    />
                  </>
                }
              </Box>

              <Box mt={4}>
                {orderId &&
                  invoiceStore?.advancePayments &&
                  invoiceStore?.advancePayments?.length > 0 && (
                    <AdvancePaymentTable
                      data={invoiceStore.advancePayments || []}
                      handleDeleteAdvancePayment={(id: number) => {
                        handleDeletePayment(id);
                      }}
                      handleEditAdvancePayment={handleEditAdvancePayment}
                    />
                  )}
              </Box>

              <Box mt={4}>
                {orderId &&
                  orderStore.orderRequest.order_products &&
                  orderStore.orderRequest.order_products?.length > 0 && (
                    <PackConfigTable
                      packs={orderStore.packs || []}
                      handleDeletePack={(id: number) => {
                        handleDeletePack(id);
                      }}
                      handleImageUpload={(pack: PackProduct) => {
                        setPackForImageUpload(pack);
                      }}
                      toggleAddProductsPopup={toggleAddPacksPopup}
                    />
                  )}
                <Dialog
                  open={!!packForImageUpload}
                  onClose={() => {
                    setPackForImageUpload(null);
                  }}
                  maxWidth="md"
                  fullWidth
                  sx={{
                    "& .MuiDialog-paper": {
                      padding: 4,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <CustomTypography variant="heading4" fontWeight={700}>
                      {orderStore.packs.find(
                        (pack) => pack.id === packForImageUpload?.id,
                      )?.label || "Pack Images"}
                    </CustomTypography>
                    <IconButton
                      onClick={() => {
                        setPackForImageUpload(null);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, paddingBottom: 2 }}>
                    <Box
                      sx={{
                        flex: 1.5,
                      }}
                    >
                      <ImageCarousel
                        images={
                          packForImageUpload?.image_url
                            ? [
                              {
                                image_url: packForImageUpload.image_url,
                              },
                            ]
                            : []
                        }
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      {isLoading ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "50%",
                            gap: 2,
                          }}
                        >
                          <CircularProgress size={40} />
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                          >
                            Uploading image...
                          </CustomTypography>
                        </Box>
                      ) : (
                        <ImageUpload
                          size="large"
                          showImagesList={false}
                          uploadText="Drop your files here, or browse"
                          addImagesToBeUploaded={(files: File[]) =>
                            packForImageUpload &&
                            handleUpload(files, packForImageUpload.id!)
                          }
                          disabled={isLoading}
                        />
                      )}
                    </Box>
                  </Box>
                </Dialog>
              </Box>

              <Box mt={4}>
                {orderId &&
                  ((orderStore.orderRequest.order_products &&
                    orderStore.orderRequest.order_products?.length > 0) ||
                    (orderStore.orderRequest.order_services &&
                      orderStore.orderRequest.order_services?.length > 0)) && (
                    <OrderCalculationsTable
                      orderDetails={orderStore.orderRequest}
                    />
                  )}
              </Box>

              <Box mt={4}>
                {orderId && (
                  <OrderPaymentLinksTable
                    paymentLinks={orderStore.existingOrder?.payment_links || []}
                    onRefresh={async (paymentLinkId: number) => {
                      try {
                        await orderStore.refreshPaymentLink(paymentLinkId);
                      } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error("Failed to refresh payment link", error);
                      }
                    }}
                    onCreatePaymentLink={handleOpenPaymentLinkDialog}
                  />
                )}
              </Box>

              <Box mt={4}>
                {orderId && projectStore.projectRequest?.length > 0 && (
                  <>
                    <ProjectRequestTable
                      list={projectStore.projectRequest}
                      onApproveOrReject={(requestId, action) => {
                        setApproveOrRejectProjectReqData({
                          requestId,
                          action,
                          isOpen: true,
                        });
                      }}
                    />
                    {approveOrRejectProjectReqData.isOpen && (
                      <>
                        {(approveOrRejectProjectReqData.action ===
                          ProjectRequestStatusId.APPROVED ||
                          approveOrRejectProjectReqData.action ===
                          ProjectRequestAction.FINANCE_APPROVE) && (
                            <ConfirmationPopup
                              zIndex={2100}
                              open={approveOrRejectProjectReqData.isOpen}
                              id={approveOrRejectProjectReqData.requestId!}
                              title={
                                approveOrRejectProjectReqData.action ===
                                  ProjectRequestAction.FINANCE_APPROVE
                                  ? "Finance Approval"
                                  : "Confirm Action"
                              }
                              content={
                                approveOrRejectProjectReqData.action ===
                                  ProjectRequestAction.FINANCE_APPROVE
                                  ? "Are you sure you want to mark this project request as finance approved?"
                                  : "Are you sure you want to approve this request?"
                              }
                              taskAssigned={
                                approveOrRejectProjectReqData.action ===
                                ProjectRequestStatusId.APPROVED
                              }
                              onConfirm={(id) => {
                                const pendingAction =
                                  approveOrRejectProjectReqData.action!;
                                setApproveOrRejectProjectReqData({
                                  ...approveOrRejectProjectReqData,
                                  isOpen: false,
                                });
                                handleApproveOrRejectProjectRequest(
                                  id,
                                  pendingAction,
                                );
                              }}
                              onCancel={() =>
                                setApproveOrRejectProjectReqData({
                                  ...approveOrRejectProjectReqData,
                                  isOpen: false,
                                })
                              }
                            />
                          )}
                        {(approveOrRejectProjectReqData.action ===
                          ProjectRequestStatusId.REJECTED ||
                          approveOrRejectProjectReqData.action ===
                          ProjectRequestAction.FINANCE_REJECT) && (
                            <SingleInput
                              zIndex={2100}
                              buttonMarginTop={0}
                              open={approveOrRejectProjectReqData.isOpen}
                              type="text"
                              heading={
                                approveOrRejectProjectReqData.action ===
                                  ProjectRequestAction.FINANCE_REJECT
                                  ? "Finance Rejection"
                                  : "Cancel Request"
                              }
                              content={
                                approveOrRejectProjectReqData.action ===
                                  ProjectRequestAction.FINANCE_REJECT
                                  ? "Please provide a reason for finance rejection"
                                  : "Please provide a reason for cancellation"
                              }
                              onSubmit={(reason) => {
                                const pendingAction =
                                  approveOrRejectProjectReqData.action!;
                                const requestId =
                                  approveOrRejectProjectReqData.requestId!;
                                setApproveOrRejectProjectReqData({
                                  ...approveOrRejectProjectReqData,
                                  isOpen: false,
                                });
                                handleApproveOrRejectProjectRequest(
                                  requestId,
                                  pendingAction,
                                  reason,
                                );
                              }}
                              onClose={() =>
                                setApproveOrRejectProjectReqData({
                                  ...approveOrRejectProjectReqData,
                                  isOpen: false,
                                })
                              }
                            />
                          )}
                      </>
                    )}
                  </>
                )}
              </Box>

              {orderId &&
                ((orderStore.orderRequest.order_products &&
                  orderStore.orderRequest.order_products?.length > 0) ||
                  (orderStore.orderRequest.order_services &&
                    orderStore.orderRequest.order_services?.length > 0)) && (
                  <Box mt={4} mb={4}>
                    <Card
                      sx={{
                        boxShadow:
                          "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px",
                        overflow: "hidden",
                      }}
                    >
                      {/* Header */}
                      <Box
                        sx={{
                          px: 4,
                          pt: 3,
                          pb: 2,
                          borderBottom: "1px solid #F1F5F9",
                        }}
                      >
                        <CustomTypography variant="heading4" fontWeight={700}>
                          Actions
                        </CustomTypography>
                      </Box>

                      {/* Order management group */}
                      {(userStore.hasPermission(EmployeeRoles.CREATE_ORDER) ||
                        userStore.hasPermission(EmployeeRoles.EDIT_ORDER)) && (
                          <Box sx={{ px: 4, pt: 3, pb: 2 }}>
                            <Box
                              component="span"
                              sx={{
                                fontSize: "0.63rem",
                                color: "#94A3B8",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                              }}
                            >
                              Order Management
                            </Box>
                            <Box
                              sx={{
                                mt: 1.5,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                              }}
                            >
                              <AppButton
                                label="Advance Payment"
                                onClick={() =>
                                  setOpenAdvancePaymentPopup(
                                    !openAdvancePaymentPopup,
                                  )
                                }
                                variant="grayscale"
                              />
                              <AppButton
                                label="Recalculate"
                                onClick={() => recalculateOrder()}
                                variant="grayscale"
                              />
                              <AppButton
                                label="Duplicate Order"
                                onClick={() => handleDuplicateOrder()}
                                variant="grayscale"
                              />
                              <AppButton
                                label="Order Discount"
                                onClick={() =>
                                  setOpenOrderDiscountPopup(!openOrderDiscountPopup)
                                }
                                variant="grayscale"
                              />
                              {userStore.hasPermission(
                                EmployeeRoles.UPDATE_ORDER_CURRENCY_RATE,
                              ) && (
                                  <AppButton
                                    label="Exchange Rate"
                                    onClick={() =>
                                      setOpenExchangeRatePopup(!openExchangeRatePopup)
                                    }
                                    variant="grayscale"
                                  />
                                )}
                            </Box>
                          </Box>
                        )}

                      {/* Downloads & Invoice group */}
                      {(userStore.hasPermission(EmployeeRoles.VIEW_ORDER_PANDL) ||
                        userStore.hasPermission(
                          EmployeeRoles.CREATE_INVOICE_AND_PI,
                        ) ||
                        userStore.hasPermission(
                          EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
                        ) ||
                        (orderStore.existingOrder?.size_mix_url &&
                          orderStore.existingOrder.size_mix_url.length > 0)) && (
                          <Box
                            sx={{
                              px: 4,
                              pt: 2,
                              pb: 2,
                              borderTop: "1px solid #F1F5F9",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                fontSize: "0.63rem",
                                color: "#94A3B8",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                              }}
                            >
                              Downloads &amp; Invoice
                            </Box>
                            <Box
                              sx={{
                                mt: 1.5,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                              }}
                            >
                              {(userStore.hasPermission(EmployeeRoles.EDIT_ORDER) ||
                                userStore.hasPermission(
                                  EmployeeRoles.CREATE_ORDER,
                                )) &&
                                userStore.hasPermission(
                                  EmployeeRoles.VIEW_ORDER_PANDL,
                                ) && (
                                  <AppButton
                                    label="Download P&L"
                                    onClick={() => handleDownloadPAndL()}
                                    variant="grayscale"
                                  />
                                )}
                              {userStore.hasPermission(
                                EmployeeRoles.CREATE_INVOICE_AND_PI,
                              ) && (
                                  <AppButton
                                    label="Send Tax Invoice Mail"
                                    onClick={() =>
                                      setConfirmationPopup(!confirmationPopup)
                                    }
                                    variant="grayscale"
                                  />
                                )}
                              {userStore.hasPermission(
                                EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
                              ) && (
                                  <AppButton
                                    label="Proforma Invoice"
                                    onClick={() => handleDownloadPerformaInvoice()}
                                    variant="grayscale"
                                  />
                                )}
                              {userStore.hasPermission(
                                EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
                              ) && (
                                  <AppButton
                                    label="Download Invoice"
                                    onClick={() => handleDownloadInvoiceByOrderId()}
                                    variant="grayscale"
                                  />
                                )}
                              {userStore.hasPermission(
                                EmployeeRoles.DOWNLOAD_INVOICE_AND_PI,
                              ) && (
                                  <AppButton
                                    label="Download Old Invoice"
                                    onClick={() =>
                                      handleDownloadOldInvoiceByOrderId()
                                    }
                                    variant="grayscale"
                                  />
                                )}
                              {orderStore.existingOrder &&
                                orderStore.existingOrder.size_mix_url &&
                                orderStore.existingOrder.size_mix_url.length >
                                0 && (
                                  <AppButton
                                    label="Download Size Details"
                                    onClick={() =>
                                      handleDownloadSizeMix(
                                        orderStore.existingOrder!.size_mix_url!,
                                      )
                                    }
                                    variant="grayscale"
                                  />
                                )}
                            </Box>
                          </Box>
                        )}

                      {/* Requests group */}
                      {(userStore.hasPermission(
                        EmployeeRoles.CREATE_STOCK_CHECK_REQUEST,
                      ) ||
                        userStore.hasPermission([
                          EmployeeRoles.CREATE_ORDER,
                          EmployeeRoles.CREATE_PROJECT_REQUEST,
                        ])) && (
                          <Box
                            sx={{
                              px: 4,
                              pt: 2,
                              pb: 2,
                              borderTop: "1px solid #F1F5F9",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                fontSize: "0.63rem",
                                color: "#94A3B8",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                              }}
                            >
                              Requests
                            </Box>
                            <Box
                              sx={{
                                mt: 1.5,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1.5,
                              }}
                            >
                              {userStore.hasPermission(
                                EmployeeRoles.CREATE_STOCK_CHECK_REQUEST,
                              ) &&
                                (orderStore.existingOrder
                                  ?.is_stock_check_required === null ||
                                  orderStore.existingOrder
                                    ?.is_stock_check_required === true) && (
                                  <AppButton
                                    label="Raise Stock Check Request"
                                    onClick={() => fetchStockChecEligibleProducts()}
                                    variant="grayscale"
                                  />
                                )}
                              {userStore.hasPermission([
                                EmployeeRoles.CREATE_ORDER,
                                EmployeeRoles.CREATE_PROJECT_REQUEST,
                              ]) && (
                                  <AppButton
                                    label="Raise Project Request"
                                    onClick={() => {
                                      const isStockCheckRequired =
                                        orderStore.existingOrder
                                          ?.is_stock_check_required === null ||
                                        orderStore.existingOrder
                                          ?.is_stock_check_required === true;

                                      if (
                                        isStockCheckRequired &&
                                        !stockStore.orderStockCheckReqDetails
                                      ) {
                                        showError(
                                          "Error Occurred",
                                          "First, Create Stock request before proceeding",
                                        );
                                        return;
                                      }
                                      setOpenProjectRequestConfirmationPopup(true);
                                    }}
                                    variant="grayscale"
                                  />
                                )}
                            </Box>
                          </Box>
                        )}

                      {/* Danger zone */}
                      {(userStore.hasPermission(EmployeeRoles.CREATE_ORDER) ||
                        userStore.hasPermission(EmployeeRoles.EDIT_ORDER)) && (
                          <Box
                            sx={{
                              px: 4,
                              pt: 2,
                              pb: 3,
                              borderTop: "1px solid #FEE2E2",
                              backgroundColor: "#FFF8F8",
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                fontSize: "0.63rem",
                                color: "#EF4444",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                minWidth: 80,
                              }}
                            >
                              Danger Zone
                            </Box>
                            <AppButton
                              label="Delete Order"
                              onClick={() =>
                                setDeleteOrderConfirmationPopup(
                                  !deleteOrderConfirmationPopup,
                                )
                              }
                              variant="grayscale"
                              customStyles={{
                                color: "#EF4444",
                                borderColor: "#FECACA",
                                backgroundColor: "#FFF",
                                "&:hover": {
                                  backgroundColor: "#FEE2E2",
                                  borderColor: "#EF4444",
                                },
                              }}
                            />
                          </Box>
                        )}
                    </Card>
                  </Box>
                )}

              {(userStore.hasPermission(EmployeeRoles.CREATE_ORDER) ||
                userStore.hasPermission(EmployeeRoles.EDIT_ORDER)) &&
                !orderId && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "end",
                      gap: 2,
                      mr: 3,
                      mb: 3,
                      flexDirection: { xs: "column", md: "row" },
                    }}
                  >
                    <AppButton
                      variant="primary"
                      label="Create Order"
                      onClick={() => CreateOrUpdateOrder()}
                      customStyles={{ minWidth: "120px" }}
                    />
                    {!orderId && (
                      <AppButton
                        variant="grayscale"
                        label="Clear"
                        onClick={() => orderStore.reset()}
                        customStyles={{ minWidth: "120px" }}
                      />
                    )}
                  </Box>
                )}

              {openProjectRequestConfirmationPopup && (
                <ConfirmationPopup
                  id={Number(orderId)}
                  open={openProjectRequestConfirmationPopup}
                  title="Confirm Action"
                  content="Please make sure billing and shipping address are added to the order"
                  onConfirm={() => {
                    if (!orderStore.existingOrder?.type_id) {
                      showError(
                        "Error Occurred",
                        "Please select the order type before proceeding.",
                      );
                      return;
                    }

                    setOpenProjectRequestConfirmationPopup(false);
                    projectStore.getTypeOfShipments();
                    projectStore.getModeOfShipments();
                    projectStore.getPaymentStatuses();
                    setOpenProjectReqPopup(true);
                  }}
                  onCancel={() => setOpenProjectRequestConfirmationPopup(false)}
                />
              )}

              {confirmationPopup && (
                <ConfirmationPopup
                  open={confirmationPopup}
                  id={Number(orderId)}
                  title="Confirm Action"
                  content="Are you sure you want to send the Tax Invoice Mail?"
                  onConfirm={() => {
                    setConfirmationPopup(!confirmationPopup);
                    handleSendTaxInvoice();
                  }}
                  onCancel={() => setConfirmationPopup(!confirmationPopup)}
                />
              )}
              {deleteOrderConfirmationPopup && (
                <ConfirmationPopup
                  open={deleteOrderConfirmationPopup}
                  id={Number(orderId)}
                  title="Confirm Action"
                  content="Are you sure you want to delete this order?"
                  onConfirm={() => {
                    setDeleteOrderConfirmationPopup(
                      !deleteOrderConfirmationPopup,
                    );
                    handleDeleteOrder();
                  }}
                  onCancel={() =>
                    setDeleteOrderConfirmationPopup(!deleteOrderConfirmationPopup)
                  }
                />
              )}

              <Box>
                {openAddProductsPopup && (
                  <AddProductPopup
                    onSubmit={(selectedProducts) =>
                      handleAddProducts(selectedProducts)
                    }
                    isLoading={isLoadingForPopup}
                    onClose={() => {
                      setOpenAddProductsPopup(!openAddProductsPopup);
                    }}
                  />
                )}
              </Box>

              <Box>
                {openAddPacksConfigurationPopup && (
                  <AddPackConfigPopup
                    list={orderStore.orderRequest.order_products || []}
                    onSubmit={(packs) => {
                      const transformedPacks = packs.products.map((product) => ({
                        product_id: product.product_id,
                        quantity: product.quantity,
                      }));
                      handleAddPacks({
                        label: packs.label,
                        quantity: packs.quantity,
                        products: transformedPacks,
                      });
                    }}
                    onClose={toggleAddPacksPopup}
                    handleInvoiceQuantity={true}
                    isLoading={isLoadingForPopup} // optional: if your popup shows a loader
                  />
                )}
              </Box>

              <Box>
                {openServicesPopus && (
                  <AddServicePopup
                    onSubmit={(selectedServices) =>
                      handleAddServices(selectedServices)
                    }
                    isLoading={isLoadingForPopup}
                    onClose={() => {
                      setOpenopenServicesPopus(!openServicesPopus);
                    }}
                  />
                )}
              </Box>
              <Box>
                <SelectAddressPopup
                  label="Select Billing Address"
                  id="billingAddress"
                  selectedAddress={orderStore.orderRequest.billing_address!}
                  list={addressStore.addresses || []}
                  toggleAddressPopup={() => {
                    setOpenAddressPopup(!openAddressPopup);
                  }}
                  open={openAddressPopup}
                  onDone={(add) => {
                    if (add) {
                      orderStore.updateBillingAddress(add);
                    }
                  }}
                  addNewAddressbtnOnClick={() => {
                    setOpenAddressPopup(!openAddressPopup);
                    handleOpenEditAddressPopup("addBilling");
                  }}
                />
              </Box>
              <Box>
                <SelectAddressPopup
                  label="Select Shipping Address"
                  id="shippingAddress"
                  selectedAddress={
                    orderStore.orderRequest.shipping_address || null
                  }
                  list={addressStore.addresses || []}
                  toggleAddressPopup={() => {
                    setOpenShippingAddressPopup(!openShippingAddressPopup);
                  }}
                  open={openShippingAddressPopup}
                  onDone={(add) => {
                    if (add) {
                      orderStore.updateShippingAddress(add);
                    }
                  }}
                  addNewAddressbtnOnClick={() => {
                    setOpenShippingAddressPopup(!openShippingAddressPopup);
                    handleOpenEditAddressPopup("addShipping");
                  }}
                />
              </Box>
              <Box>
                <SelectAddressPopup
                  label="Select Bill From Address"
                  id="billFromAddress"
                  selectedAddress={orderStore.orderRequest.bill_from_address!}
                  list={addressStore.addresses || []}
                  toggleAddressPopup={() => {
                    setOpenBillFromAddressPopup(!openBillFromAddressPopup);
                  }}
                  open={openBillFromAddressPopup}
                  onDone={(add) => {
                    if (add) {
                      orderStore.updateBillFromAddress(add);
                    }
                  }}
                  addNewAddressbtnOnClick={() => {
                    setOpenBillFromAddressPopup(!openBillFromAddressPopup);
                    handleOpenEditAddressPopup("addBillFrom");
                  }}
                  disableAddButton={true}
                />
              </Box>
              <ExchangeRatePopup
                open={openExchangeRatePopup}
                onClose={() => setOpenExchangeRatePopup(false)}
                onSave={(data) => {
                  handleUpdateExchangeRate(
                    Number(data.exchangeRate),
                    data.currency,
                  );
                }}
              />

              <SingleInput
                open={openOrderDiscountPopup}
                initialValue={
                  String(orderStore.orderRequest?.discount_percentage) || "0"
                }
                heading="Order Discount"
                type="number"
                content="Order Discount%"
                onClose={() => {
                  setOpenOrderDiscountPopup(!openOrderDiscountPopup);
                }}
                onSubmit={(value) => {
                  if (value) {
                    handleUpdateOrderDiscount(Number(value));
                  }
                }}
              />

              <SingleInput
                open={openUpdateAllQuantitiesPopup}
                initialValue={"0"}
                heading="Update All Quantities"
                type="number"
                content="Update All Quantities"
                onClose={() => {
                  setOpenUpdateAllQuantitiesPopup(!openUpdateAllQuantitiesPopup);
                }}
                onSubmit={(value) => {
                  if (value) {
                    handleUpdateAllQuantites(parseInt(value));
                  }
                }}
              />

              {openAdvancePaymentPopup && (
                <AddPaymentInfoPopup
                  onClose={() => {
                    setOpenAdvancePaymentPopup(false);
                    setEditingAdvancePayment(undefined);
                  }}
                  onSubmit={(paymentInfo: InvoicePaymentRequest) =>
                    updateAdvancePayment(paymentInfo)
                  }
                  editAdvancePayment={editingAdvancePayment}
                />
              )}

              <Modal
                open={openEditAddressPopup}
                onClose={(event, reason) => {
                  if (reason !== "backdropClick") {
                    setOpenEditAddressPopup(false);
                  }
                }}
                aria-labelledby="edit-address-modal-title"
                aria-describedby="edit-address-modal-description"
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    maxHeight: "80vh",
                    overflow: "auto",
                    bgcolor: "background.paper",
                    p: 0,
                    borderRadius: "4px",
                    boxShadow: 24,
                    width: "50vw",
                  }}
                >
                  <IconButton
                    onClick={() => setOpenEditAddressPopup(false)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 10,
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  <AddEditAddress
                    editaddressId={editAddressId}
                    selectedCustomer={
                      orderStore.orderRequest.customer_id
                        ? [
                          {
                            id: String(orderStore.orderRequest.customer_id),
                            value: orderStore.orderRequest.customer_name || "",
                          },
                        ]
                        : []
                    }
                    paddingBottomForPopup={1}
                    paddingTopForModal={0}
                    marginBottomForPopup={0}
                    isNavigationNeeded={false}
                    onClose={() => {
                      setOpenEditAddressPopup(false);
                    }}
                    {...(popupType === "billing" && {
                      getUpdatedBillingAddress: (res) => {
                        orderStore.updateBillingAddress(res);
                        if (
                          res?.id === orderStore.orderRequest.shipping_address_id
                        ) {
                          orderStore.updateShippingAddress(res);
                        }
                        addressStore.clearAddresses();
                      },
                    })}
                    {...(popupType === "shipping" && {
                      getUpdatedShippingAddress: (res) => {
                        orderStore.updateShippingAddress(res);
                        if (
                          res?.id === orderStore.orderRequest.billing_address_id
                        ) {
                          orderStore.updateBillingAddress(res);
                        }
                        addressStore.clearAddresses();
                      },
                    })}
                    {...(popupType === "addBilling" && {
                      getUpdatedBillingAddress: (res) => {
                        orderStore.updateBillingAddress(res);
                        addressStore.clearAddresses();
                      },
                    })}
                    {...(popupType === "addShipping" && {
                      getUpdatedShippingAddress: (res) => {
                        orderStore.updateShippingAddress(res);
                        addressStore.clearAddresses();
                      },
                    })}
                    setLoadingForPopup={(loading) => {
                      setIsLoadingForPopup(loading);
                    }}
                  />
                </Box>
              </Modal>
              <Modal
                open={openCustomersPopup}
                onClose={() => setOpenCustomersPopup(false)}
                aria-labelledby="edit-customer-modal-title"
                aria-describedby="edit-customer-modal-description"
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    maxHeight: "80vh",
                    overflow: "auto",
                    bgcolor: "background.paper",
                    p: 0,
                    borderRadius: "4px",
                    boxShadow: 24,
                    width: "60vw",
                  }}
                >
                  <IconButton
                    onClick={() => setOpenCustomersPopup(false)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 10,
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  <AddCustomer
                    setLoadingForPopup={(loading) => {
                      setIsLoadingForPopup(loading);
                    }}
                    isNavigationNeeded={false}
                    onClose={(customer: Customer) => {
                      const createdCustomer = customer
                        ? [
                          {
                            id: String(customer.id),
                            value: customer.name || "",
                          },
                        ]
                        : [];

                      orderStore.selectCustomer(createdCustomer, customer);
                      setOpenCustomersPopup(false);
                    }}
                  />
                </Box>
              </Modal>
              <Modal
                sx={{ zIndex: 2000 }}
                open={openUsersPopup}
                onClose={() => setOpenUsersPopup(false)}
                aria-labelledby="edit-users-modal-title"
                aria-describedby="edit-users-modal-description"
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    maxHeight: "80vh",
                    overflow: "auto",
                    bgcolor: "background.paper",
                    p: 0,
                    borderRadius: "4px",
                    boxShadow: 24,
                    width: "50vw",
                  }}
                >
                  <IconButton
                    onClick={() => setOpenUsersPopup(false)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 10,
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  <AddUser
                    customer={
                      orderStore.orderRequest.customer_id
                        ? [
                          {
                            id: String(orderStore.orderRequest.customer_id),
                            value: `${orderStore.orderRequest.customer_name || ""
                              }`,
                          },
                        ]
                        : []
                    }
                    setLoadingForPopup={(loading) => {
                      setIsLoadingForPopup(loading);
                    }}
                    isNavigationNeeded={false}
                    onClose={(user: NewUser) => {
                      if (
                        Number(user.customer_id) ===
                        Number(orderStore.orderRequest.customer_id)
                      ) {
                        orderStore.updateUser(user);
                        searchUserByCustomerId(
                          Number(orderStore.orderRequest.customer_id),
                        );
                      }
                      setOpenUsersPopup(false);
                    }}
                  />
                </Box>
              </Modal>
              {stockStore.eligibleProducts.length > 0 && (
                <RaiseStockCheckReqPopup
                  data={stockStore.eligibleProducts}
                  open={openRaiseStockCheckReqPopup}
                  onClose={() =>
                    setOpenRaiseStockCheckReqPopup(!openRaiseStockCheckReqPopup)
                  }
                  onSave={(data) => {
                    if (data) {
                      handleStockCheckrequest(data);
                    }
                  }}
                />
              )}
              {selectedStockCheckRequestId && openRaiseStockCheckDetailsPopup && (
                <StockCheckDetailsPopup
                  product={selectedProduct!}
                  requestId={selectedStockCheckRequestId}
                  isViewOnly={
                    stockStore.orderStockCheckReqDetails.filter(
                      (item) => item.id === selectedStockCheckRequestId,
                    )[0].status !== 1
                  }
                  open={openRaiseStockCheckDetailsPopup}
                  onClose={() =>
                    setOpenRaiseStockCheckDetailsPopup(
                      !openRaiseStockCheckDetailsPopup,
                    )
                  }
                  onSubmit={(requestId, product, suggestedProduct) => {
                    updateStockCheck(requestId, product, suggestedProduct);
                  }}
                />
              )}
              {orderStore.orderRequest.order_products &&
                orderStore.orderRequest.order_products.length > 0 && (
                  <ProjectRequestPopup
                    open={openProjectReqPopup}
                    stockCheckNotRequired={
                      orderStore.existingOrder?.is_stock_check_required === false
                    }
                    currentFormData={{
                      brand_name: orderStore.orderRequest?.customer_name || "",
                      client_poc_contact_no: orderStore.orderRequest?.user
                        ? orderStore.orderRequest.user.phone
                        : "",
                      client_poc: orderStore.orderRequest?.user
                        ? `${orderStore.orderRequest.user.first_name} ${orderStore.orderRequest.user.last_name || ""
                        }`
                        : "",
                      company_name: orderStore.orderRequest?.customer_name || "",
                      delivery_date: null,
                      dispatch_date: null,
                      amount: "",
                      purchase_order: "",
                      is_delivery_date_flexible: "",
                      shipping_type_id: "",
                      shipment_mode_id: "",
                      payment_status_id: "",
                      payment_term: "",
                      is_diy_redemption: "",
                      mockup_status_id: "",
                      order_type_id:
                        orderStore.orderRequest?.type_id?.toString() || "",
                      order_additional_info: "",
                      warehouse_id: null,
                    }}
                    expedited_fees_added={
                      orderStore.orderRequest?.services?.some(
                        (service) => service.name === "Expedited Fees",
                      ) || false
                    }
                    onClose={() => setOpenProjectReqPopup(!openProjectReqPopup)}
                    onSubmit={(data) => {
                      handleProjectRequest(data);
                    }}
                  />
                )}
              {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) &&
                mockupModalOpen &&
                mockupProductDetails?.id && (
                  <OrderUnifiedMockupModal
                    open={mockupModalOpen}
                    onClose={closeProductMockupModal}
                    activeTab={mockupModalTab}
                    onTabChange={handleMockupTabChange}
                    mockupId={generatorMockupId}
                    productId={mockupProductDetails.id}
                    productName={mockupProductDetails.name}
                    archived={0}
                    canDeleteMockup={userStore.hasPermission(
                      EmployeeRoles.DELETE_MOCKUP,
                    )}
                    mockups={orderStore.productMockup || []}
                    comments={orderStore.mockupComment || []}
                    updateMockupComment={(mockupComment) => {
                      setIsCommentsFetching(true);
                      postMockupComment(mockupComment);
                    }}
                    fetchMockupComment={(mockupId: number) => {
                      setIsCommentsFetching(true);
                      fetchMockupComment(mockupId);
                    }}
                    isCommentsFetching={isCommentsFetching}
                    deleteMockup={(mockupId: number) => {
                      deleteMockup(mockupId);
                    }}
                    isMainMockupImageLoading={isMainMockupImage}
                    onSelectPsdId={(id) => {
                      mockupConfigStore.resetGeneratedMockupData();
                      mockupConfigStore.setSelectedPsdId(id);
                    }}
                    onGenerateMockup={handleOrderGenerateMockup}
                    onUploadLogoToLibrary={handleOrderUploadLogoToLibrary}
                    onDeleteUploadedLogo={handleOrderDeleteUploadedLogo}
                    onManualUploadMockup={
                      generatorMockupId
                        ? handleOrderManualUploadMockup
                        : undefined
                    }
                    onUploadMockupFiles={handleOrderUploadMockupFiles}
                  />
                )}
              <ImageGalleryPopup
                key={"productImages"}
                open={openImageGalleryModal}
                onClose={() => setOpenImageGalleryModal(false)}
                images={
                  mockupProductDetails?.image_url
                    ? [
                      {
                        id: mockupProductDetails.id,
                        src: mockupProductDetails.image_url,
                      },
                    ]
                    : []
                }
                title={"Product Images"}
              />
              <UploadClientPOPopup
                open={openUploadClientPOPopup}
                onClose={() => setOpenUploadClientPOPopup(false)}
                onSave={handleSaveClientPO}
              />

              <ActivityLogDrawer
                isOpen={isActivityDrawerOpen}
                onClose={() => setIsActivityDrawerOpen(false)}
                activityLogs={orderStore.activityLogs}
                isLoading={isActivityLoading}
                title="Activity History"
                subtitle={orderId ? `Order #${orderId}` : null}
              />
            </Box>
          </Grid2>
        )}
    </>
  );
};

export default observer(AddOrder);
